// ═══════════════════════════════════════════════════
//  ReconAI Pro v3 – Main Application
// ═══════════════════════════════════════════════════
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './styles/app.css';
import { generateId, formatCurrency, formatDate, parseDateToISO, downloadJSON, downloadCSV } from './utils/helpers';
import { parsePdfFile, parseDelimitedText, detectTxType } from './parsers/pdfParser';
import { parseExcelFile, downloadExcelTemplate, exportReconciliationExcel, exportPdfToExcel } from './parsers/excelParser';
import { runMatching, computeMatchScore, DEFAULT_TOLERANCES, detectDuplicates } from './engine/matchingEngine';

// ══════════════════════════════════════════════════
//  CATEGORY SYSTEM
// ══════════════════════════════════════════════════
const DEFAULT_CATEGORIES = [
  { id: 'purchase', label: 'Purchase', color: '#2563eb' },
  { id: 'sales', label: 'Sales', color: '#7c3aed' },
  { id: 'payment', label: 'Payment', color: '#0d9488' },
  { id: 'receipt', label: 'Receipt', color: '#059669' },
  { id: 'debit_note', label: 'Debit Note', color: '#dc2626' },
  { id: 'credit_note', label: 'Credit Note', color: '#ea580c' },
  { id: 'excess_shortage', label: 'Excess / Shortage', color: '#d97706' },
  { id: 'jobwork', label: 'Jobwork', color: '#4f46e5' },
  { id: 'journal', label: 'Journal', color: '#64748b' },
  { id: 'tds', label: 'TDS', color: '#be185d' },
  { id: 'adjustment', label: 'Adjustment', color: '#78716c' },
  { id: 'round_off', label: 'Round Off', color: '#a8a29e' },
  { id: 'opening_balance', label: 'Opening Balance', color: '#0284c7' },
  { id: 'other', label: 'Other', color: '#6b7280' },
];

// Opposite-party mapping: company category → supplier category
const DEFAULT_CATEGORY_MAPPING = {
  purchase: 'sales',
  sales: 'purchase',
  payment: 'receipt',
  receipt: 'payment',
  debit_note: 'credit_note',
  credit_note: 'debit_note',
  excess_shortage: 'debit_note',
  jobwork: 'purchase',
  journal: 'journal',
  tds: 'tds',
  adjustment: 'adjustment',
  round_off: 'round_off',
  opening_balance: 'opening_balance',
  other: 'other',
};

// Map transaction type string to category id
const typeToCategoryId = (type) => {
  if (!type) return 'other';
  const t = type.toLowerCase();
  if (t.includes('purchase')) return 'purchase';
  if (t.includes('sale')) return 'sales';
  if (t.includes('payment')) return 'payment';
  if (t.includes('receipt')) return 'receipt';
  if (t.includes('debit note')) return 'debit_note';
  if (t.includes('credit note')) return 'credit_note';
  if (t.includes('excess') || t.includes('shortage')) return 'excess_shortage';
  if (t.includes('jobwork')) return 'jobwork';
  if (t.includes('journal')) return 'journal';
  if (t.includes('tds')) return 'tds';
  if (t.includes('expense')) return 'purchase'; // Expense Voucher → Purchase category
  if (t.includes('round')) return 'round_off';
  return 'other';
};

// ══════════════════════════════════════════════════
//  SHARED COMPONENTS
// ══════════════════════════════════════════════════
const Toast = ({ toasts, onDismiss }) => (
  <div className="toast-container">
    {toasts.map(t => (
      <div key={t.id} className={`toast toast-${t.type}`} onClick={() => onDismiss(t.id)}>
        {t.type === 'success' ? '✓' : t.type === 'error' ? '✕' : 'ℹ'} {t.message}
      </div>
    ))}
  </div>
);

const Modal = ({ title, children, actions, onClose }) => (
  <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
    <div className="modal-box">
      <div className="modal-title">{title}</div>
      {children}
      {actions && <div className="modal-actions">{actions}</div>}
    </div>
  </div>
);

const ScoreBar = ({ score, label }) => {
  const cls = score >= 80 ? 'score-high' : score >= 60 ? 'score-medium' : 'score-low';
  return (
    <div className={`score-bar ${cls}`}>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', minWidth: 60 }}>{label}</span>
      <div className="score-bar-track"><div className="score-bar-fill" style={{ width: `${score}%` }} /></div>
      <span className="score-bar-label">{score}%</span>
    </div>
  );
};

const ConfidenceBadge = ({ value }) => {
  const cls = value >= 80 ? 'badge-success' : value >= 60 ? 'badge-warning' : 'badge-danger';
  return <span className={`badge ${cls}`}>{value}%</span>;
};

// ══════════════════════════════════════════════════
//  DEMO DATA GENERATOR
// ══════════════════════════════════════════════════
const createDemoSession = () => {
  const companyLedger = [
    { id: generateId(), date: '2025-04-15', docNo: 'FI109', narration: 'Purchase Invoice FI109/25-26 Fayaleen Industries', debit: 0, credit: 449385, type: 'Purchase', ref: 'FI109', confidence: 95 },
    { id: generateId(), date: '2025-04-20', docNo: 'FI112', narration: 'Purchase Invoice FI112/25-26', debit: 0, credit: 621338, type: 'Purchase', ref: 'FI112', confidence: 95 },
    { id: generateId(), date: '2025-05-05', docNo: 'FI113', narration: 'Purchase Invoice FI113/25-26', debit: 0, credit: 384426, type: 'Purchase', ref: 'FI113', confidence: 95 },
    { id: generateId(), date: '2025-05-10', docNo: 'FI115', narration: 'Purchase Invoice FI115/25-26', debit: 0, credit: 655746, type: 'Purchase', ref: 'FI115', confidence: 95 },
    { id: generateId(), date: '2025-06-01', docNo: 'FI120', narration: 'Purchase Invoice FI120/25-26', debit: 0, credit: 620953, type: 'Purchase', ref: 'FI120', confidence: 95 },
    { id: generateId(), date: '2025-06-10', docNo: 'FI121', narration: 'Purchase Invoice FI121/25-26', debit: 0, credit: 498960, type: 'Purchase', ref: 'FI121', confidence: 95 },
    { id: generateId(), date: '2025-07-01', docNo: 'FI123', narration: 'Purchase Invoice FI123/25-26', debit: 0, credit: 602619, type: 'Purchase', ref: 'FI123', confidence: 95 },
    { id: generateId(), date: '2025-07-15', docNo: 'FI125', narration: 'Purchase Invoice FI125/25-26', debit: 0, credit: 507404, type: 'Purchase', ref: 'FI125', confidence: 95 },
    { id: generateId(), date: '2025-04-10', docNo: 'JW329', narration: 'Jobwork charges JW329', debit: 218751, credit: 0, type: 'Jobwork', ref: 'JW329', confidence: 90 },
    { id: generateId(), date: '2025-04-25', docNo: 'JW351', narration: 'Jobwork charges JW351', debit: 346147.37, credit: 0, type: 'Jobwork', ref: 'JW351', confidence: 90 },
    { id: generateId(), date: '2025-05-20', docNo: 'JW368', narration: 'Jobwork charges JW368', debit: 352487, credit: 0, type: 'Jobwork', ref: 'JW368', confidence: 90 },
    { id: generateId(), date: '2025-06-15', docNo: 'JW433', narration: 'Jobwork charges JW433', debit: 337189, credit: 0, type: 'Jobwork', ref: 'JW433', confidence: 90 },
    { id: generateId(), date: '2025-04-20', docNo: 'CH719570', narration: 'Payment by cheque CH719570', debit: 230634, credit: 0, type: 'Payment', ref: 'CH719570', confidence: 95 },
    { id: generateId(), date: '2025-05-15', docNo: 'CH719601', narration: 'Payment by cheque CH719601', debit: 177459, credit: 0, type: 'Payment', ref: 'CH719601', confidence: 95 },
    { id: generateId(), date: '2025-06-10', docNo: 'CH767495', narration: 'Payment NEFT CH767495', debit: 221929, credit: 0, type: 'Payment', ref: 'CH767495', confidence: 95 },
    { id: generateId(), date: '2025-06-25', docNo: 'CH767534', narration: 'Payment RTGS CH767534', debit: 305989, credit: 0, type: 'Payment', ref: 'CH767534', confidence: 95 },
    { id: generateId(), date: '2025-06-20', docNo: 'DN65', narration: 'Debit Note DN65 - quality issue', debit: 1657, credit: 0, type: 'Debit Note', ref: 'DN65', confidence: 90 },
    { id: generateId(), date: '2025-07-30', docNo: 'DN73', narration: 'Debit Note DN73 - shortage', debit: 184224, credit: 0, type: 'Debit Note', ref: 'DN73', confidence: 90 },
    { id: generateId(), date: '2025-05-10', docNo: 'ESV61', narration: 'Excess material ESV61', debit: 0, credit: 865.20, type: 'Excess', ref: 'ESV61', confidence: 85 },
    { id: generateId(), date: '2025-07-20', docNo: 'GST001', narration: 'GST non-filing provision JV', debit: 42799, credit: 0, type: 'Journal', ref: 'GST001', confidence: 80 },
    { id: generateId(), date: '2025-08-01', docNo: 'CONS01', narration: 'Consumable charges', debit: 3059, credit: 0, type: 'Journal', ref: 'CONS01', confidence: 80 },
  ];

  const supplierLedger = [
    { id: generateId(), date: '2025-04-15', docNo: 'FI109', narration: 'Sales Invoice FI109 to Pavos Tex', debit: 449385, credit: 0, type: 'Sales', ref: 'FI109', confidence: 95 },
    { id: generateId(), date: '2025-04-20', docNo: 'FI112', narration: 'Sales Invoice FI112', debit: 621338, credit: 0, type: 'Sales', ref: 'FI112', confidence: 95 },
    { id: generateId(), date: '2025-05-05', docNo: 'FI113', narration: 'Sales Invoice FI113', debit: 384426, credit: 0, type: 'Sales', ref: 'FI113', confidence: 95 },
    { id: generateId(), date: '2025-05-10', docNo: 'FI115', narration: 'Sales Invoice FI115', debit: 655746, credit: 0, type: 'Sales', ref: 'FI115', confidence: 95 },
    { id: generateId(), date: '2025-06-01', docNo: 'FI120', narration: 'Sales Invoice FI120', debit: 620953.20, credit: 0, type: 'Sales', ref: 'FI120', confidence: 95 },
    { id: generateId(), date: '2025-06-10', docNo: 'FI121', narration: 'Sales Invoice FI121', debit: 498960, credit: 0, type: 'Sales', ref: 'FI121', confidence: 95 },
    { id: generateId(), date: '2025-07-01', docNo: 'FI123', narration: 'Sales Invoice FI123', debit: 602619, credit: 0, type: 'Sales', ref: 'FI123', confidence: 95 },
    { id: generateId(), date: '2025-07-15', docNo: 'FI125', narration: 'Sales Invoice FI125', debit: 507404, credit: 0, type: 'Sales', ref: 'FI125', confidence: 95 },
    { id: generateId(), date: '2025-09-20', docNo: 'FI129', narration: 'Sales Invoice FI129 (new)', debit: 743161, credit: 0, type: 'Sales', ref: 'FI129', confidence: 95 },
    { id: generateId(), date: '2025-04-20', docNo: 'R91', narration: 'Receipt R91 - CH719570', debit: 0, credit: 230634, type: 'Receipt', ref: 'R91', confidence: 95 },
    { id: generateId(), date: '2025-05-15', docNo: 'R95', narration: 'Receipt R95 - CH719601', debit: 0, credit: 177459, type: 'Receipt', ref: 'R95', confidence: 95 },
    { id: generateId(), date: '2025-06-10', docNo: 'R98', narration: 'Receipt R98 - CH767495', debit: 0, credit: 221929, type: 'Receipt', ref: 'R98', confidence: 95 },
    { id: generateId(), date: '2025-06-25', docNo: 'R108', narration: 'Receipt R108 - CH767534', debit: 0, credit: 305989, type: 'Receipt', ref: 'R108', confidence: 95 },
    { id: generateId(), date: '2025-06-20', docNo: 'CN65', narration: 'Credit Note CN65 - quality issue', debit: 0, credit: 1657, type: 'Credit Note', ref: 'CN65', confidence: 90 },
    { id: generateId(), date: '2025-05-10', docNo: 'ESV61', narration: 'Debit Note for excess material', debit: 865, credit: 0, type: 'Debit Note', ref: 'ESV61', confidence: 85 },
    { id: generateId(), date: '2025-11-05', docNo: 'DN_FY028', narration: 'Debit Note DN/FY/028 - pending settlement', debit: 343487, credit: 0, type: 'Debit Note', ref: 'DN_FY028', confidence: 90 },
  ];

  return {
    id: generateId(), supplier: 'FAYALEEN INDUSTRIES', company: 'PAVOS TEX PVT LTD',
    period: 'Apr 2025 - Jan 2026', recoDate: '2026-01-31',
    createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    status: 'in-progress', companyLedger, supplierLedger,
    matchResult: null, recoItems: [], actionItems: [], notes: '',
    tolerances: { ...DEFAULT_TOLERANCES },
    categoryMapping: { ...DEFAULT_CATEGORY_MAPPING },
    customCategories: [],
  };
};

// ══════════════════════════════════════════════════
//  MAIN APP
// ══════════════════════════════════════════════════
// ══════════════════════════════════════════════════
//  PDF → EXCEL CONVERTER COMPONENT
// ══════════════════════════════════════════════════
function PdfToExcelConverter({ toast, onLoadIntoSession }) {
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState([]); // [{filename, transactions, metadata, strategy, error}]
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState('');

  const handleFiles = async (e) => {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;
    setFiles(selected);
    setResults([]);
    setProcessing(true);
    const allResults = [];

    for (let fi = 0; fi < selected.length; fi++) {
      const file = selected[fi];
      setProgress(`Processing ${fi + 1}/${selected.length}: ${file.name}...`);
      try {
        const result = await parsePdfFile(file);
        allResults.push({
          filename: file.name,
          transactions: result.transactions,
          metadata: result.metadata,
          strategy: result.strategyUsed,
          lineCount: result.lineCount,
          pageCount: result.pageCount,
          error: null,
        });
      } catch (err) {
        allResults.push({ filename: file.name, transactions: [], metadata: {}, strategy: '', error: err.message });
      }
    }

    setResults(allResults);
    setProcessing(false);
    setProgress('');
    const total = allResults.reduce((s, r) => s + r.transactions.length, 0);
    const failed = allResults.filter(r => r.error).length;
    if (total > 0) toast(`Converted ${total} transactions from ${allResults.length - failed} file(s)`, 'success');
    if (failed > 0) toast(`${failed} file(s) failed to convert`, 'error');
  };

  const downloadExcel = (result) => {
    const fn = result.filename.replace(/\.pdf$/i, '') + '_converted';
    exportPdfToExcel(result.transactions, result.metadata, fn);
    toast(`Downloaded ${fn}.xlsx`);
  };

  const downloadAll = () => {
    const allTx = results.filter(r => !r.error).flatMap(r => r.transactions);
    const meta = results.find(r => r.metadata)?.metadata || {};
    exportPdfToExcel(allTx, meta, 'PDF_All_Converted');
    toast(`Downloaded PDF_All_Converted.xlsx with ${allTx.length} transactions`);
  };

  return (
    <div>
      {/* Upload Zone */}
      <div className="upload-zone" style={{ padding: 30, textAlign: 'center', border: '2px dashed var(--teal)', borderRadius: 8, background: 'var(--bg)', marginBottom: 20 }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>📄 → 📊</div>
        <h3 style={{ marginBottom: 8 }}>PDF to Excel Converter</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Upload ledger PDFs (Tally, SAP, wibserp, any ERP). Extracts transactions and downloads as formatted Excel.
        </p>
        <input type="file" multiple accept=".pdf" onChange={handleFiles} disabled={processing} style={{ marginBottom: 8 }} />
        {processing && <div style={{ marginTop: 12, color: 'var(--teal)', fontWeight: 600 }}>{progress}</div>}
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div>
          {results.length > 1 && results.some(r => !r.error) && (
            <div className="flex-between mb-2">
              <h4>{results.filter(r => !r.error).reduce((s, r) => s + r.transactions.length, 0)} total transactions from {results.filter(r => !r.error).length} file(s)</h4>
              <button className="btn btn-teal" onClick={downloadAll}>⬇ Download All as One Excel</button>
            </div>
          )}

          {results.map((result, idx) => (
            <div key={idx} className="card mb-2" style={{ padding: 16, border: result.error ? '1px solid var(--red)' : '1px solid var(--border)' }}>
              <div className="flex-between" style={{ marginBottom: result.error ? 0 : 12 }}>
                <div>
                  <strong>{result.filename}</strong>
                  {!result.error && (
                    <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 8 }}>
                      {result.transactions.length} transactions | {result.pageCount} pages | Strategy: {result.strategy}
                    </span>
                  )}
                </div>
                {!result.error && (
                  <div className="flex gap-1">
                    {onLoadIntoSession && (
                      <button className="btn btn-outline btn-sm" onClick={() => onLoadIntoSession(result.transactions, result.metadata)} title="Load directly into current session">
                        📥 Load into Session
                      </button>
                    )}
                    <button className="btn btn-teal btn-sm" onClick={() => downloadExcel(result)}>⬇ Download Excel</button>
                  </div>
                )}
              </div>

              {result.error && (
                <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8, whiteSpace: 'pre-wrap' }}>{result.error}</div>
              )}

              {!result.error && result.transactions.length > 0 && (
                <div className="table-wrap" style={{ maxHeight: 250, overflowY: 'auto' }}>
                  <table style={{ fontSize: 11 }}>
                    <thead><tr><th>Date</th><th>Particulars</th><th>Vch Type</th><th>Vch No.</th><th>Debit</th><th>Credit</th></tr></thead>
                    <tbody>
                      {result.transactions.slice(0, 50).map((t, ti) => (
                        <tr key={ti}>
                          <td>{formatDate(t.date)}</td>
                          <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.narration}</td>
                          <td><span className="badge badge-neutral" style={{ fontSize: 9 }}>{t.type}</span></td>
                          <td><strong>{t.docNo}</strong></td>
                          <td className="currency">{t.debit ? formatCurrency(t.debit) : '—'}</td>
                          <td className="currency">{t.credit ? formatCurrency(t.credit) : '—'}</td>
                        </tr>
                      ))}
                      {result.transactions.length > 50 && (
                        <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 11 }}>...and {result.transactions.length - 50} more rows (all included in Excel download)</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {!result.error && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, display: 'flex', gap: 16 }}>
                  <span>Total DR: {formatCurrency(result.transactions.reduce((s, t) => s + (t.debit || 0), 0))}</span>
                  <span>Total CR: {formatCurrency(result.transactions.reduce((s, t) => s + (t.credit || 0), 0))}</span>
                  {result.metadata?.openingBalance != null && <span>Opening: {formatCurrency(Math.abs(result.metadata.openingBalance))} {result.metadata.openingBalance < 0 ? 'CR' : 'DR'}</span>}
                  {result.metadata?.closingBalance != null && <span>Closing: {formatCurrency(Math.abs(result.metadata.closingBalance))} {result.metadata.closingBalance < 0 ? 'CR' : 'DR'}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════
//  TOOLS PAGE
// ══════════════════════════════════════════════════
function ToolsPage({ onBack, toast }) {
  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px 16px' }}>
      <div className="flex-between mb-2">
        <div>
          <h2 style={{ margin: 0 }}>🔧 Tools</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 0' }}>Utility tools for ledger data conversion and processing</p>
        </div>
        <button className="btn btn-outline" onClick={onBack}>← Back to Sessions</button>
      </div>

      <div className="card" style={{ padding: 20 }}>
        <PdfToExcelConverter toast={toast} />
      </div>
    </div>
  );
}

// ── Password Hashing (SHA-256, browser-native) ──
const hashPassword = async (password) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password + '_reconai_salt_v3');
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
};

// ── Initialize default admin on first run ──
const initUsers = async () => {
  const saved = localStorage.getItem('reconai_v3_users');
  if (saved) return JSON.parse(saved);
  const adminHash = await hashPassword('admin123');
  const defaultUsers = [{ id: 'admin_001', username: 'admin', passwordHash: adminHash, role: 'admin', createdAt: new Date().toISOString() }];
  localStorage.setItem('reconai_v3_users', JSON.stringify(defaultUsers));
  return defaultUsers;
};

// ── Login Page Component ──
function LoginPage({ onLogin, toast }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) { toast('Enter username and password', 'error'); return; }
    setLoading(true);
    try {
      const users = await initUsers();
      const pwHash = await hashPassword(password);
      const user = users.find(u => u.username.toLowerCase() === username.toLowerCase().trim() && u.passwordHash === pwHash);
      if (user) {
        const session = { id: user.id, username: user.username, role: user.role };
        sessionStorage.setItem('reconai_v3_currentUser', JSON.stringify(session));
        onLogin(session);
      } else {
        toast('Invalid username or password', 'error');
      }
    } catch (err) { toast('Login failed: ' + err.message, 'error'); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0d9488 0%, #1e3a5f 100%)' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 40, width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#0d9488' }}>⚖ Vendor <span style={{ color: '#1e3a5f' }}>Reco</span> Tool</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Ledger Reconciliation System v3</div>
        </div>
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Username</label>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder="Enter username" autoFocus
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password"
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <button type="submit" disabled={loading}
            style={{ width: '100%', padding: '12px', background: '#0d9488', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Admin Page Component (User Management) ──
function AdminPage({ currentUser, onBack, toast }) {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState('');
  const [newPass, setNewPass] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [changePwUser, setChangePwUser] = useState(null);
  const [changePwVal, setChangePwVal] = useState('');

  useEffect(() => {
    try { const u = JSON.parse(localStorage.getItem('reconai_v3_users') || '[]'); setUsers(u); } catch { setUsers([]); }
  }, []);

  const saveUsers = (u) => { setUsers(u); localStorage.setItem('reconai_v3_users', JSON.stringify(u)); };

  const addUser = async () => {
    if (!newUser.trim() || !newPass.trim()) { toast('Enter username and password', 'error'); return; }
    if (users.find(u => u.username.toLowerCase() === newUser.toLowerCase().trim())) { toast('Username already exists', 'error'); return; }
    const pwHash = await hashPassword(newPass);
    const user = { id: generateId(), username: newUser.trim(), passwordHash: pwHash, role: newRole, createdAt: new Date().toISOString() };
    saveUsers([...users, user]);
    setNewUser(''); setNewPass(''); setNewRole('user');
    toast(`User "${user.username}" created as ${user.role}`);
  };

  const deleteUser = (id) => {
    if (id === currentUser.id) { toast('Cannot delete yourself', 'error'); return; }
    const u = users.find(x => x.id === id);
    if (u && u.role === 'admin' && users.filter(x => x.role === 'admin').length <= 1) { toast('Cannot delete the last admin', 'error'); return; }
    if (window.confirm(`Delete user "${u?.username}"?`)) { saveUsers(users.filter(x => x.id !== id)); toast('User deleted'); }
  };

  const changePassword = async (id) => {
    if (!changePwVal.trim()) { toast('Enter new password', 'error'); return; }
    const pwHash = await hashPassword(changePwVal);
    saveUsers(users.map(u => u.id === id ? { ...u, passwordHash: pwHash } : u));
    setChangePwUser(null); setChangePwVal('');
    toast('Password changed');
  };

  const changeRole = (id, role) => {
    const u = users.find(x => x.id === id);
    if (u && u.role === 'admin' && role !== 'admin' && users.filter(x => x.role === 'admin').length <= 1) { toast('Cannot remove the last admin', 'error'); return; }
    saveUsers(users.map(x => x.id === id ? { ...x, role } : x));
    toast(`Role changed to ${role}`);
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div className="flex-between mb-2">
        <h2>User Management</h2>
        <button className="btn btn-outline" onClick={onBack}>← Back</button>
      </div>

      {/* Add New User */}
      <div className="card mb-2" style={{ padding: 16 }}>
        <h4 style={{ marginBottom: 12 }}>Add New User</h4>
        <div className="flex gap-1" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Username</label>
            <input type="text" value={newUser} onChange={e => setNewUser(e.target.value)} placeholder="Username" style={{ width: 160 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Password</label>
            <input type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Password" style={{ width: 160 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Role</label>
            <select value={newRole} onChange={e => setNewRole(e.target.value)} style={{ width: 120 }}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button className="btn btn-teal" onClick={addUser}>+ Add User</button>
        </div>
      </div>

      {/* User List */}
      <div className="card" style={{ padding: 16 }}>
        <h4 style={{ marginBottom: 12 }}>All Users ({users.length})</h4>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Username</th><th>Role</th><th>Created</th><th>Actions</th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ background: u.id === currentUser.id ? 'rgba(13,148,136,0.06)' : '' }}>
                  <td><strong>{u.username}</strong> {u.id === currentUser.id && <span className="badge badge-info" style={{ fontSize: 9, marginLeft: 4 }}>YOU</span>}</td>
                  <td>
                    <select value={u.role} onChange={e => changeRole(u.id, e.target.value)} style={{ width: 100, padding: '4px 8px', fontWeight: 600, color: u.role === 'admin' ? '#0d9488' : '#6b7280' }}>
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                    </select>
                  </td>
                  <td style={{ fontSize: 12 }}>{formatDate(u.createdAt)}</td>
                  <td>
                    <div className="flex gap-1">
                      {changePwUser === u.id ? (
                        <>
                          <input type="password" value={changePwVal} onChange={e => setChangePwVal(e.target.value)} placeholder="New password" style={{ width: 120 }} />
                          <button className="btn btn-success btn-xs" onClick={() => changePassword(u.id)}>Save</button>
                          <button className="btn btn-outline btn-xs" onClick={() => { setChangePwUser(null); setChangePwVal(''); }}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-outline btn-xs" onClick={() => setChangePwUser(u.id)}>Change Password</button>
                          {u.id !== currentUser.id && <button className="btn btn-danger btn-xs" onClick={() => deleteUser(u.id)}>Delete</button>}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [page, setPage] = useState('home');
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [dark, setDark] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({});
  const sessionSaveCount = useRef(0);

  // ── RBAC: Current User ──
  const [currentUser, setCurrentUser] = useState(() => {
    try { const u = sessionStorage.getItem('reconai_v3_currentUser'); return u ? JSON.parse(u) : null; } catch { return null; }
  });
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    try {
      const saved = localStorage.getItem('reconai_v3_sessions');
      if (saved) setSessions(JSON.parse(saved));
      else setSessions([createDemoSession()]);
    } catch { setSessions([createDemoSession()]); }
    if (localStorage.getItem('reconai_dark') === 'true') { setDark(true); document.documentElement.setAttribute('data-theme', 'dark'); }
  }, []);

  useEffect(() => {
    sessionSaveCount.current++;
    // Skip the first render (sessions = [] before load effect populates from localStorage)
    if (sessionSaveCount.current <= 1) return;
    // Persist ALL changes including empty array (admin deleted all sessions)
    try { localStorage.setItem('reconai_v3_sessions', JSON.stringify(sessions)); } catch { }
  }, [sessions]);

  const toast = useCallback((message, type = 'success') => {
    const id = generateId();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.setAttribute('data-theme', next ? 'dark' : '');
    localStorage.setItem('reconai_dark', next);
  };

  const updateSession = (updated) => {
    updated.updatedAt = new Date().toISOString();
    setSessions(prev => prev.map(s => s.id === updated.id ? updated : s));
    setCurrentSession(updated);
  };

  const deleteSession = (id) => {
    if (!isAdmin) { toast('Only admin can delete sessions', 'error'); return; }
    setSessions(prev => prev.filter(s => s.id !== id));
    if (currentSession?.id === id) { setCurrentSession(null); setPage('home'); }
    toast('Session deleted');
  };

  const logout = () => {
    sessionStorage.removeItem('reconai_v3_currentUser');
    setCurrentUser(null);
    setPage('home');
    setCurrentSession(null);
  };

  const openSession = (session) => { setCurrentSession(session); setPage('workbench'); };
  const startNew = () => { setWizardStep(1); setWizardData({}); setPage('wizard'); };

  const completeWizard = () => {
    const d = wizardData;
    const newSession = {
      id: generateId(), supplier: d.supplier || 'Unknown', company: d.company || 'Unknown',
      period: `${formatDate(d.periodFrom)} - ${formatDate(d.periodTo)}`,
      recoDate: d.recoDate || new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      status: 'draft', companyLedger: d.companyLedger || [], supplierLedger: d.supplierLedger || [],
      matchResult: null, recoItems: [], actionItems: [], notes: '',
      tolerances: d.tolerances || { ...DEFAULT_TOLERANCES },
      categoryMapping: d.categoryMapping || { ...DEFAULT_CATEGORY_MAPPING },
      customCategories: d.customCategories || [],
    };
    setSessions(prev => [...prev, newSession]);
    openSession(newSession);
    toast('Session created successfully');
  };

  // ── Show login screen if not authenticated ──
  if (!currentUser) {
    return (
      <>
        <LoginPage onLogin={setCurrentUser} toast={toast} />
        <Toast toasts={toasts} onDismiss={id => setToasts(prev => prev.filter(t => t.id !== id))} />
      </>
    );
  }

  return (
    <div className="app-layout">
      <header className="topbar">
        <div className="topbar-logo">⚖ Vendor <span>Reco</span> Tool <span style={{ fontSize: 12, opacity: 0.6, marginLeft: 4 }}>v3</span></div>
        <div className="topbar-right">
          {page !== 'home' && <button className="btn btn-ghost" style={{ color: 'rgba(255,255,255,0.8)' }} onClick={() => setPage('home')}>← Sessions</button>}
          <button className="btn-icon" style={{ color: 'white', border: '1px solid rgba(255,255,255,0.2)' }} onClick={() => setPage('tools')} title="PDF to Excel Converter & Tools">🔧 Tools</button>
          <button className="btn-icon" style={{ color: 'white', border: '1px solid rgba(255,255,255,0.2)' }} onClick={startNew}>+ New</button>
          {isAdmin && <button className="btn-icon" style={{ color: '#fbbf24', border: '1px solid rgba(251,191,36,0.4)', fontWeight: 700 }} onClick={() => setPage('admin')} title="User Management">👥 Admin</button>}
          <button className="btn-icon" style={{ color: 'white', border: '1px solid rgba(255,255,255,0.2)' }} onClick={toggleDark}>{dark ? '☀' : '🌙'}</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8, paddingLeft: 8, borderLeft: '1px solid rgba(255,255,255,0.2)' }}>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
              {currentUser.username} <span style={{ fontSize: 10, padding: '1px 5px', background: isAdmin ? 'rgba(251,191,36,0.3)' : 'rgba(255,255,255,0.15)', borderRadius: 4 }}>{currentUser.role.toUpperCase()}</span>
            </span>
            <button className="btn-icon" style={{ color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.15)', fontSize: 11, padding: '4px 10px' }} onClick={logout} title="Logout">Logout</button>
          </div>
        </div>
      </header>

      <div className="main-content">
        {page === 'home' && <HomePage sessions={sessions} onNew={startNew} onOpen={openSession} onDelete={isAdmin ? deleteSession : null} onExport={s => { downloadJSON(s, `recon_${s.supplier}_${s.recoDate}.json`); toast('Exported'); }} onImport={file => {
          const r = new FileReader(); r.onload = e => { try { const d = JSON.parse(e.target.result); setSessions(p => [...p, Array.isArray(d) ? d[0] : d]); toast('Imported'); } catch { toast('Import failed', 'error'); } }; r.readAsText(file);
        }} />}

        {page === 'wizard' && <WizardPage step={wizardStep} onStepChange={setWizardStep} data={wizardData} onDataChange={setWizardData} onComplete={completeWizard} onCancel={() => setPage('home')} toast={toast} />}

        {page === 'workbench' && currentSession && <WorkbenchPage session={currentSession} onUpdate={updateSession} onBack={() => setPage('home')} toast={toast} />}

        {page === 'tools' && <ToolsPage onBack={() => setPage('home')} toast={toast} />}

        {page === 'admin' && isAdmin && <AdminPage currentUser={currentUser} onBack={() => setPage('home')} toast={toast} />}
      </div>

      <Toast toasts={toasts} onDismiss={id => setToasts(prev => prev.filter(t => t.id !== id))} />
    </div>
  );
}

// ══════════════════════════════════════════════════
//  HOME PAGE
// ══════════════════════════════════════════════════
function HomePage({ sessions, onNew, onOpen, onDelete, onExport, onImport }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const filtered = sessions.filter(s => {
    const ms = !search || s.supplier.toLowerCase().includes(search.toLowerCase()) || s.company.toLowerCase().includes(search.toLowerCase());
    const mf = filter === 'all' || s.status === filter;
    return ms && mf;
  });
  const stats = {
    total: sessions.length,
    completed: sessions.filter(s => s.status === 'completed' || s.status === 'approved').length,
    pending: sessions.filter(s => s.status === 'draft' || s.status === 'in-progress').length,
    variance: sessions.reduce((sum, s) => {
      const cb = (s.companyLedger || []).reduce((a, t) => a + (t.debit || 0) - (t.credit || 0), 0);
      const sb = (s.supplierLedger || []).reduce((a, t) => a + (t.debit || 0) - (t.credit || 0), 0);
      return sum + Math.abs(cb - sb);
    }, 0),
  };

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total Sessions</div><div className="stat-value">{stats.total}</div></div>
        <div className="stat-card green"><div className="stat-label">Completed</div><div className="stat-value">{stats.completed}</div></div>
        <div className="stat-card amber"><div className="stat-label">Pending</div><div className="stat-value">{stats.pending}</div></div>
        <div className="stat-card red"><div className="stat-label">Total Variance</div><div className="stat-value">{formatCurrency(stats.variance)}</div></div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Reconciliation Sessions</div>
          <div className="flex gap-2">
            <input type="text" placeholder="Search supplier..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 200 }} />
            <select value={filter} onChange={e => setFilter(e.target.value)} style={{ width: 140 }}>
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="approved">Approved</option>
            </select>
            <button className="btn btn-teal" onClick={onNew}>+ New Session</button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <div className="empty-title">No sessions found</div>
            <div className="empty-desc">Create your first reconciliation session to get started.</div>
            <button className="btn btn-teal" onClick={onNew}>Start New Reconciliation</button>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Supplier</th><th>Company</th><th>Period</th><th>Status</th><th>Company Txns</th><th>Supplier Txns</th><th>Match Rate</th><th>Updated</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map(s => {
                  const matchRate = s.matchResult ? s.matchResult.summary.autoMatchRate : '—';
                  return (
                    <tr key={s.id}>
                      <td><strong>{s.supplier}</strong></td>
                      <td>{s.company}</td>
                      <td>{s.period}</td>
                      <td><span className={`badge badge-${s.status === 'completed' || s.status === 'approved' ? 'success' : s.status === 'in-progress' ? 'warning' : 'info'}`}>{s.status}</span></td>
                      <td className="text-right">{(s.companyLedger || []).length}</td>
                      <td className="text-right">{(s.supplierLedger || []).length}</td>
                      <td className="text-right">{matchRate !== '—' ? matchRate + '%' : '—'}</td>
                      <td>{formatDate(s.updatedAt)}</td>
                      <td>
                        <div className="flex gap-1">
                          <button className="btn btn-primary btn-xs" onClick={() => onOpen(s)}>Open</button>
                          <button className="btn btn-outline btn-xs" onClick={() => onExport(s)}>Export</button>
                          {onDelete && <button className="btn btn-danger btn-xs" onClick={() => { if (window.confirm('Delete this session?')) onDelete(s.id); }}>✕</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex gap-2 mt-2" style={{ alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Import session:</span>
          <input type="file" accept=".json" onChange={e => { if (e.target.files[0]) { onImport(e.target.files[0]); e.target.value = ''; } }} style={{ width: 'auto', border: 'none', padding: 0 }} />
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
//  WIZARD PAGE
// ══════════════════════════════════════════════════
function WizardPage({ step, onStepChange, data, onDataChange, onComplete, onCancel, toast }) {
  const update = useCallback((key, val) => onDataChange(prev => ({ ...prev, [key]: val })), [onDataChange]);
  const updateBatch = useCallback((updates) => onDataChange(prev => ({ ...prev, ...updates })), [onDataChange]);
  const steps = ['Info', 'Company', 'Supplier', 'Configure', 'Review'];

  return (
    <div className="card">
      <div className="wizard-steps">
        {steps.map((label, i) => (
          <React.Fragment key={i}>
            <div className={`wizard-step ${step === i + 1 ? 'active' : ''} ${step > i + 1 ? 'completed' : ''}`}>
              <div className="wizard-step-num">{step > i + 1 ? '✓' : i + 1}</div>
              <div className="wizard-step-label">{label}</div>
            </div>
            {i < 4 && <div className={`wizard-connector ${step > i + 1 ? 'completed' : ''}`} />}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && <WizardStep1 data={data} update={update} />}
      {step === 2 && <LedgerUpload data={data} update={update} updateBatch={updateBatch} side="company" toast={toast} />}
      {step === 3 && <LedgerUpload data={data} update={update} updateBatch={updateBatch} side="supplier" toast={toast} />}
      {step === 4 && <WizardStep4 data={data} update={update} />}
      {step === 5 && <WizardStep5 data={data} />}

      <div className="flex-between mt-3">
        <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
        <div className="flex gap-1">
          <button className="btn btn-outline" onClick={() => onStepChange(Math.max(1, step - 1))} disabled={step === 1}>← Previous</button>
          {step < 5 ? (
            <button className="btn btn-teal" onClick={() => onStepChange(step + 1)}>Next →</button>
          ) : (
            <button className="btn btn-success" onClick={onComplete}>✓ Create Session</button>
          )}
        </div>
      </div>
    </div>
  );
}

function WizardStep1({ data, update }) {
  return (
    <div>
      <h2 style={{ marginBottom: 20 }}>Step 1: Basic Information</h2>
      <div className="side-by-side">
        <div className="form-group"><label>Company Name</label><input type="text" value={data.company || ''} onChange={e => update('company', e.target.value)} placeholder="e.g., PAVOS TEX PVT LTD" /></div>
        <div className="form-group"><label>Supplier / Counterparty Name</label><input type="text" value={data.supplier || ''} onChange={e => update('supplier', e.target.value)} placeholder="e.g., FAYALEEN INDUSTRIES" /></div>
      </div>
      <div className="side-by-side">
        <div className="form-group"><label>Period From</label><input type="date" value={data.periodFrom || ''} onChange={e => update('periodFrom', e.target.value)} /></div>
        <div className="form-group"><label>Period To</label><input type="date" value={data.periodTo || ''} onChange={e => update('periodTo', e.target.value)} /></div>
      </div>
      <div className="form-group"><label>Reconciliation Date</label><input type="date" value={data.recoDate || ''} onChange={e => update('recoDate', e.target.value)} /></div>
    </div>
  );
}

function LedgerUpload({ data, update, updateBatch, side, toast }) {
  const [method, setMethod] = useState('pdf');
  const [pasteText, setPasteText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [status, setStatus] = useState('');
  const ledgerKey = side === 'company' ? 'companyLedger' : 'supplierLedger';
  const ledger = data[ledgerKey] || [];
  const title = side === 'company' ? 'Company Ledger' : 'Supplier Ledger';
  const color = side === 'company' ? 'var(--navy)' : 'var(--teal)';
  const balance = ledger.reduce((s, t) => s + (t.debit || 0) - (t.credit || 0), 0);

  const handlePdf = async (e) => {
    const files = Array.from(e.target.files); if (!files.length) return;
    setParsing(true); setStatus(`Extracting text from ${files.length} file(s)...`);
    try {
      let allTransactions = [];
      let combinedMeta = {};
      let totalLines = 0;
      let strategiesUsed = new Set();

      for (const file of files) {
        const result = await parsePdfFile(file);
        allTransactions.push(...result.transactions);
        totalLines += result.lineCount;
        strategiesUsed.add(result.strategyUsed);
        Object.assign(combinedMeta, result.metadata);
      }

      // Duplicate upload protection: warn if existing ledger has similar count
      if (ledger.length > 0 && Math.abs(ledger.length - allTransactions.length) < 3) {
        if (!window.confirm(`This side already has ${ledger.length} transactions. Uploading will REPLACE them with ${allTransactions.length} new transactions. Continue?`)) {
          setParsing(false); return;
        }
      }
      setStatus(`✓ ${allTransactions.length} transactions from ${totalLines} lines (${Array.from(strategiesUsed).join(', ')})`);
      const updates = { [ledgerKey]: allTransactions };
      if (combinedMeta.company && side === 'company') updates.company = combinedMeta.company;
      if (combinedMeta.supplier) updates.supplier = combinedMeta.supplier;
      if (combinedMeta.period) {
        const pp = combinedMeta.period.match(/(\S+)\s+to\s+(\S+)/i);
        if (pp) { const f = parseDateToISO(pp[1]), t = parseDateToISO(pp[2]); if (f) updates.periodFrom = f; if (t) { updates.periodTo = t; updates.recoDate = t; } }
      }
      // Store opening and closing balances from parser metadata
      const obKey = side === 'company' ? 'openingBalanceCompany' : 'openingBalanceSupplier';
      const cbKey = side === 'company' ? 'closingBalanceCompany' : 'closingBalanceSupplier';
      if (combinedMeta.openingBalance != null) updates[obKey] = combinedMeta.openingBalance;
      if (combinedMeta.closingBalance != null) updates[cbKey] = combinedMeta.closingBalance;
      updateBatch(updates);
      toast(`Imported ${allTransactions.length} transactions from ${files.length} PDF(s)`, 'success');
    } catch (err) { setStatus('✕ ' + err.message); toast(err.message, 'error'); }
    finally { setParsing(false); e.target.value = ''; }
  };

  const handlePaste = () => {
    const parsed = parseDelimitedText(pasteText);
    if (parsed.length > 0) { update(ledgerKey, parsed); toast(`Parsed ${parsed.length} transactions`); setPasteText(''); }
    else toast('No transactions found in pasted data', 'error');
  };

  const handleExcel = async (e) => {
    const files = Array.from(e.target.files); if (!files.length) return;
    setParsing(true); setStatus(`Parsing ${files.length} Excel file(s)...`);
    try {
      let allTransactions = [];
      let totalLines = 0;
      
      let combinedMeta = {};
      for (const file of files) {
        const result = await parseExcelFile(file);
        allTransactions.push(...result.transactions);
        totalLines += result.lineCount;
        if (result.metadata) Object.assign(combinedMeta, result.metadata);
      }

      setStatus(`✓ ${allTransactions.length} transactions uploaded from Excel`);
      const updates = { [ledgerKey]: allTransactions };
      // Store opening and closing balances from Excel metadata
      const obKey = side === 'company' ? 'openingBalanceCompany' : 'openingBalanceSupplier';
      const cbKey = side === 'company' ? 'closingBalanceCompany' : 'closingBalanceSupplier';
      if (combinedMeta.openingBalance != null) updates[obKey] = combinedMeta.openingBalance;
      if (combinedMeta.closingBalance != null) updates[cbKey] = combinedMeta.closingBalance;
      updateBatch(updates);
      toast(`Imported ${allTransactions.length} transactions from Excel`, 'success');
    } catch (err) { setStatus('✕ ' + err.message); toast(err.message, 'error'); }
    finally { setParsing(false); e.target.value = ''; }
  };

  const handleJson = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const json = JSON.parse(evt.target.result);
        const txns = json[side === 'company' ? 'companyLedger' : 'supplierLedger']?.transactions || json.transactions || (Array.isArray(json) ? json : []);
        if (txns.length > 0) {
          const mapped = txns.map(t => ({ id: generateId(), date: parseDateToISO(t.date) || t.date, docNo: t.docNo || t.doc_no || '', narration: t.narration || '', debit: t.debit || 0, credit: t.credit || 0, type: t.type || detectTxType(t.docNo || '', t.narration || ''), ref: t.ref || t.docNo || '', confidence: t.confidence || 80 }));
          update(ledgerKey, mapped); toast(`Imported ${mapped.length} transactions from JSON`);
        } else toast('No transactions found in JSON', 'error');
      } catch { toast('Invalid JSON file', 'error'); }
    }; reader.readAsText(file);
  };

  return (
    <div>
      <h2 style={{ marginBottom: 20 }}>Step {side === 'company' ? '2' : '3'}: {title}</h2>
      <div className="form-group">
        <label>Import Method</label>
        <select value={method} onChange={e => setMethod(e.target.value)} style={{ maxWidth: 300 }}>
          <option value="pdf">Upload PDF (Tally/SAP/Zoho/Any ERP)</option>
          <option value="excel">Upload Excel Data</option>
          <option value="csv">Paste CSV / Excel Data</option>
          <option value="json">Import JSON</option>
          <option value="pdftoexcel">🔧 Convert PDF → Excel (Download)</option>
        </select>
      </div>

      {method === 'pdftoexcel' && (
        <div style={{ border: `2px solid ${color}`, borderRadius: 8, padding: 16 }}>
          <PdfToExcelConverter toast={toast} onLoadIntoSession={(transactions, metadata) => {
            if (ledger.length > 0 && !window.confirm(`This will REPLACE existing ${ledger.length} transactions. Continue?`)) return;
            const updates = { [ledgerKey]: transactions };
            const obKey = side === 'company' ? 'openingBalanceCompany' : 'openingBalanceSupplier';
            const cbKey = side === 'company' ? 'closingBalanceCompany' : 'closingBalanceSupplier';
            if (metadata.openingBalance != null) updates[obKey] = metadata.openingBalance;
            if (metadata.closingBalance != null) updates[cbKey] = metadata.closingBalance;
            updateBatch(updates);
            toast(`Loaded ${transactions.length} transactions from PDF conversion`, 'success');
          }} />
        </div>
      )}

      {method === 'pdf' && (
        <div className="upload-zone" style={{ borderColor: color }}>
          <div className="upload-icon">📄</div>
          <div className="upload-text">Select {title} PDF</div>
          <div className="upload-hint">Supports Tally, SAP, Zoho, QuickBooks, Marg, Busy & generic formats</div>
          <input type="file" multiple accept=".pdf" onChange={handlePdf} disabled={parsing} style={{ marginTop: 12, width: 'auto' }} />
          {parsing && <div style={{ marginTop: 10, padding: 10, background: 'var(--amber-bg)', borderRadius: 6 }}>⏳ {status}</div>}
          {!parsing && status && <div style={{ marginTop: 10, padding: 10, background: status.startsWith('✓') ? 'var(--green-bg)' : 'var(--red-bg)', borderRadius: 6 }}>{status}</div>}
        </div>
      )}

      {method === 'excel' && (
        <div className="upload-zone" style={{ borderColor: color }}>
          <div className="upload-icon">📊</div>
          <div className="upload-text">Select {title} Excel File(s)</div>
          <div className="upload-hint flex gap-1" style={{ justifyContent: 'center', marginTop: 8 }}>
            Supports standard layout: DATE | DOC NO | NARRATION | DEBIT | CREDIT
            <button className="btn btn-outline btn-xs" onClick={downloadExcelTemplate} disabled={parsing}>Download Template</button>
          </div>
          <input type="file" multiple accept=".xlsx, .xls, .csv" onChange={handleExcel} disabled={parsing} style={{ marginTop: 12, width: 'auto' }} />
          {parsing && <div style={{ marginTop: 10, padding: 10, background: 'var(--amber-bg)', borderRadius: 6 }}>⏳ {status}</div>}
          {!parsing && status && <div style={{ marginTop: 10, padding: 10, background: status.startsWith('✓') ? 'var(--green-bg)' : 'var(--red-bg)', borderRadius: 6 }}>{status}</div>}
        </div>
      )}

      {method === 'csv' && (
        <div className="form-group">
          <label>Paste Data (Date | Doc No | Narration | Debit | Credit)</label>
          <textarea value={pasteText} onChange={e => setPasteText(e.target.value)} placeholder="Paste tab-separated or comma-separated data here..." rows={6} />
          <button className="btn btn-primary mt-1" onClick={handlePaste}>Parse Data</button>
        </div>
      )}

      {method === 'json' && (
        <div className="upload-zone" style={{ borderColor: color }}>
          <div className="upload-icon">📋</div>
          <div className="upload-text">Import JSON (from extract_pdf.py or exported session)</div>
          <input type="file" accept=".json" onChange={handleJson} style={{ marginTop: 12, width: 'auto' }} />
        </div>
      )}

      {ledger.length > 0 && (
        <div className="mt-2">
          {/* Opening Balance Input */}
          <div className="card mb-1" style={{ background: 'var(--bg)', padding: '12px 16px' }}>
            <div className="flex gap-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontWeight: 600, fontSize: 13, minWidth: 130 }}>Opening Balance:</label>
              <input
                type="number" step="0.01"
                value={data[side === 'company' ? 'openingBalanceCompany' : 'openingBalanceSupplier'] != null ? Math.abs(data[side === 'company' ? 'openingBalanceCompany' : 'openingBalanceSupplier']) : ''}
                onChange={e => {
                  const val = parseFloat(e.target.value) || 0;
                  const obKey = side === 'company' ? 'openingBalanceCompany' : 'openingBalanceSupplier';
                  const drCr = data[(side === 'company' ? 'openingBalTypeCo' : 'openingBalTypeSu')] || 'cr';
                  update(obKey, drCr === 'cr' ? -val : val);
                }}
                placeholder="Enter opening balance..."
                style={{ width: 160 }}
              />
              <select
                value={(data[side === 'company' ? 'openingBalanceCompany' : 'openingBalanceSupplier'] || 0) <= 0 ? 'cr' : 'dr'}
                onChange={e => {
                  const obKey = side === 'company' ? 'openingBalanceCompany' : 'openingBalanceSupplier';
                  const typeKey = side === 'company' ? 'openingBalTypeCo' : 'openingBalTypeSu';
                  const cur = Math.abs(data[obKey] || 0);
                  update(typeKey, e.target.value);
                  update(obKey, e.target.value === 'cr' ? -cur : cur);
                }}
                style={{ width: 80 }}
              >
                <option value="cr">CR</option>
                <option value="dr">DR</option>
              </select>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                {data[side === 'company' ? 'openingBalanceCompany' : 'openingBalanceSupplier'] != null
                  ? `(Detected: ${formatCurrency(Math.abs(data[side === 'company' ? 'openingBalanceCompany' : 'openingBalanceSupplier']))} ${(data[side === 'company' ? 'openingBalanceCompany' : 'openingBalanceSupplier'] || 0) <= 0 ? 'CR' : 'DR'})`
                  : '(Not detected - enter manually)'}
              </span>
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontWeight: 600, fontSize: 13, minWidth: 130 }}>Closing Balance:</label>
              <input
                type="number" step="0.01"
                value={(() => {
                  const cbKey = side === 'company' ? 'closingBalanceCompany' : 'closingBalanceSupplier';
                  return data[cbKey] != null ? Math.abs(data[cbKey]) : '';
                })()}
                onChange={e => {
                  const val = parseFloat(e.target.value) || 0;
                  const cbKey = side === 'company' ? 'closingBalanceCompany' : 'closingBalanceSupplier';
                  const curSign = (data[cbKey] || 0) <= 0 ? -1 : 1;
                  update(cbKey, curSign * val);
                }}
                placeholder="Enter closing balance..."
                style={{ width: 160 }}
              />
              <select
                value={(() => {
                  const cbKey = side === 'company' ? 'closingBalanceCompany' : 'closingBalanceSupplier';
                  return (data[cbKey] || 0) <= 0 ? 'cr' : 'dr';
                })()}
                onChange={e => {
                  const cbKey = side === 'company' ? 'closingBalanceCompany' : 'closingBalanceSupplier';
                  const cur = Math.abs(data[cbKey] || 0);
                  update(cbKey, e.target.value === 'cr' ? -cur : cur);
                }}
                style={{ width: 80 }}
              >
                <option value="cr">CR</option>
                <option value="dr">DR</option>
              </select>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                Computed: {formatCurrency(Math.abs((data[side === 'company' ? 'openingBalanceCompany' : 'openingBalanceSupplier'] || 0) + balance))} {((data[side === 'company' ? 'openingBalanceCompany' : 'openingBalanceSupplier'] || 0) + balance) <= 0 ? 'CR' : 'DR'}
              </span>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
              Net Txn Movement: {formatCurrency(balance)}
            </div>
          </div>
          <div className="flex-between mb-1">
            <strong>{ledger.length} transactions loaded</strong>
            <span className="currency" style={{ fontSize: 16, fontWeight: 600 }}>Net Movement: {formatCurrency(balance)}</span>
          </div>
          <div className="table-wrap" style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table>
              <thead><tr><th>Date</th><th>Doc No</th><th>Narration</th><th>Debit</th><th>Credit</th><th>Type</th><th>Conf</th></tr></thead>
              <tbody>
                {ledger.slice(0, 100).map(t => (
                  <tr key={t.id}>
                    <td>{formatDate(t.date)}</td>
                    <td><strong>{t.docNo}</strong></td>
                    <td style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.narration}</td>
                    <td className="currency">{t.debit ? formatCurrency(t.debit) : '—'}</td>
                    <td className="currency">{t.credit ? formatCurrency(t.credit) : '—'}</td>
                    <td><span className="badge badge-info">{t.type}</span></td>
                    <td><ConfidenceBadge value={t.confidence || 80} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {ledger.length > 100 && <div style={{ textAlign: 'center', padding: 8, color: 'var(--text-muted)', fontSize: 13 }}>Showing first 100 of {ledger.length} transactions</div>}
        </div>
      )}
    </div>
  );
}

function WizardStep4({ data, update }) {
  const tol = data.tolerances || { ...DEFAULT_TOLERANCES };
  const setTol = (key, val) => update('tolerances', { ...tol, [key]: Number(val) });
  return (
    <div>
      <h2 style={{ marginBottom: 20 }}>Step 4: Matching Configuration</h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>Configure tolerance thresholds for the auto-matching engine. These can be adjusted later from the workbench.</p>
      <div className="side-by-side">
        <div className="form-group"><label>Date Tolerance (days)</label><input type="number" value={tol.dateDays} onChange={e => setTol('dateDays', e.target.value)} min={0} max={90} /></div>
        <div className="form-group"><label>Amount Tolerance (₹)</label><input type="number" value={tol.amountAbsolute} onChange={e => setTol('amountAbsolute', e.target.value)} min={0} step={0.01} /></div>
      </div>
      <div className="side-by-side">
        <div className="form-group"><label>Amount Tolerance (%)</label><input type="number" value={tol.amountPercent} onChange={e => setTol('amountPercent', e.target.value)} min={0} max={10} step={0.1} /></div>
        <div className="form-group"><label>Narration Similarity Threshold</label><input type="number" value={tol.narrationThreshold} onChange={e => setTol('narrationThreshold', e.target.value)} min={0} max={1} step={0.05} /></div>
      </div>
      <div className="side-by-side">
        <div className="form-group"><label>Materiality Threshold (₹)</label><input type="number" value={tol.materialityThreshold} onChange={e => setTol('materialityThreshold', e.target.value)} min={0} /></div>
        <div className="form-group"><label>Max Group Size (1:N matching)</label><input type="number" value={tol.groupMaxSize} onChange={e => setTol('groupMaxSize', e.target.value)} min={2} max={20} /></div>
      </div>
    </div>
  );
}

function WizardStep5({ data }) {
  const cl = data.companyLedger || [], sl = data.supplierLedger || [];
  const cb = cl.reduce((s, t) => s + (t.debit || 0) - (t.credit || 0), 0);
  const sb = sl.reduce((s, t) => s + (t.debit || 0) - (t.credit || 0), 0);
  return (
    <div>
      <h2 style={{ marginBottom: 20 }}>Step 5: Review & Create</h2>
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(2,1fr)' }}>
        <div className="stat-card"><div className="stat-label">Company</div><div className="stat-value" style={{ fontSize: 18 }}>{data.company || '—'}</div><div className="stat-sub">{cl.length} transactions • Balance: {formatCurrency(cb)}</div></div>
        <div className="stat-card"><div className="stat-label">Supplier</div><div className="stat-value" style={{ fontSize: 18 }}>{data.supplier || '—'}</div><div className="stat-sub">{sl.length} transactions • Balance: {formatCurrency(sb)}</div></div>
      </div>
      <div className="card" style={{ background: 'var(--bg)' }}>
        <p><strong>Period:</strong> {data.periodFrom} to {data.periodTo}</p>
        <p><strong>Reco Date:</strong> {data.recoDate}</p>
        <p><strong>Tolerances:</strong> Date ±{(data.tolerances || DEFAULT_TOLERANCES).dateDays}d, Amount ±₹{(data.tolerances || DEFAULT_TOLERANCES).amountAbsolute}</p>
        <p className="mt-1" style={{ color: 'var(--teal)', fontWeight: 600 }}>Click "Create Session" to proceed to the reconciliation workbench where you can run matching.</p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
//  WORKBENCH PAGE
// ══════════════════════════════════════════════════
function WorkbenchPage({ session, onUpdate, onBack, toast }) {
  const [tab, setTab] = useState('summary');
  const [s, setS] = useState(session);
  const [matching, setMatching] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);

  useEffect(() => { setS(session); }, [session]);
  const save = () => { onUpdate(s); toast('Session saved'); };

  const runMatch = () => {
    setMatching(true);
    setTimeout(() => {
      const result = runMatching(s.companyLedger, s.supplierLedger, s.tolerances || DEFAULT_TOLERANCES, s.matchGroupByDocNo || false);
      const updated = { ...s, matchResult: result, status: 'in-progress' };
      // Convert arrays to Sets for fast lookup (arrays survive JSON serialization, Sets don't)
      const compIdSet = new Set(result.matchedCompanyIds);
      const suppIdSet = new Set(result.matchedSupplierIds);
      // Apply match status to transactions
      updated.companyLedger = s.companyLedger.map(t => ({
        ...t, matchStatus: compIdSet.has(t.id) ? (result.matchGroups.find(g => g.companyItems.includes(t.id))?.status || 'CONFIRMED') : 'UNMATCHED'
      }));
      updated.supplierLedger = s.supplierLedger.map(t => ({
        ...t, matchStatus: suppIdSet.has(t.id) ? (result.matchGroups.find(g => g.supplierItems.includes(t.id))?.status || 'CONFIRMED') : 'UNMATCHED'
      }));
      setS(updated);
      onUpdate(updated);
      setMatching(false);
      toast(`Matching complete: ${result.summary.matched} confirmed, ${result.summary.suggested} suggested, ${result.summary.unmatchedCompany + result.summary.unmatchedSupplier} unmatched`);
    }, 500);
  };

  const compNetMovement = s.companyLedger.reduce((a, t) => a + (t.debit || 0) - (t.credit || 0), 0);
  const suppNetMovement = s.supplierLedger.reduce((a, t) => a + (t.debit || 0) - (t.credit || 0), 0);
  const compBal = s.closingBalanceCompany != null ? s.closingBalanceCompany : ((s.openingBalanceCompany || 0) + compNetMovement);
  const suppBal = s.closingBalanceSupplier != null ? s.closingBalanceSupplier : ((s.openingBalanceSupplier || 0) + suppNetMovement);
  const mr = s.matchResult;

  const tabs = ['summary', 'categories', 'matching', 'manual', 'company', 'supplier', 'exceptions', 'duplicates', 'aging', 'statement', 'actions', 'notes'];

  return (
    <div>
      <div className="flex-between mb-2">
        <div>
          <h2 style={{ marginBottom: 4 }}>{s.supplier}</h2>
          <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{s.company} • {s.period} • <span className={`badge badge-${s.status === 'completed' ? 'success' : 'warning'}`}>{s.status}</span></span>
        </div>
        <div className="flex gap-1" style={{alignItems: 'center'}}>
          <label style={{display:'flex', alignItems:'center', gap:4, fontSize:13, fontWeight:600, color:'var(--teal)', marginRight: 10, cursor:'pointer'}}>
            <input type="checkbox" checked={s.matchGroupByDocNo || false} onChange={e => {
              const updated = {...s, matchGroupByDocNo: e.target.checked};
              setS(updated); onUpdate(updated);
            }} style={{transform:'scale(1.1)'}} />
            Group by Doc No before Matching
          </label>
          <button className="btn btn-teal" onClick={runMatch} disabled={matching}>{matching ? '⏳ Matching...' : '▶ Run Matching'}</button>
          <button className="btn btn-outline" onClick={save}>💾 Save</button>
          <button className="btn btn-outline" onClick={() => { downloadJSON(s, `recon_${s.supplier}.json`); toast('Exported'); }}>📤 Export</button>
          <button className="btn btn-outline" onClick={onBack}>← Back</button>
        </div>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(5,1fr)' }}>
        <div className="stat-card"><div className="stat-label">Company Txns</div><div className="stat-value">{s.companyLedger.length}</div><div className="stat-sub">{formatCurrency(compBal)}</div></div>
        <div className="stat-card"><div className="stat-label">Supplier Txns</div><div className="stat-value">{s.supplierLedger.length}</div><div className="stat-sub">{formatCurrency(suppBal)}</div></div>
        <div className="stat-card green"><div className="stat-label">Matched</div><div className="stat-value">{mr ? mr.summary.matched : '—'}</div><div className="stat-sub">{mr ? mr.summary.autoMatchRate + '%' : 'Run matching'}</div></div>
        <div className="stat-card amber"><div className="stat-label">Suggested</div><div className="stat-value">{mr ? mr.summary.suggested : '—'}</div></div>
        <div className="stat-card red"><div className="stat-label">Unmatched</div><div className="stat-value">{mr ? mr.summary.unmatchedCompany + mr.summary.unmatchedSupplier : '—'}</div></div>
      </div>

      <div className="card">
        <div className="tabs">
          {tabs.map(t => <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>)}
        </div>

        {tab === 'summary' && <SummaryView session={s} />}
        {tab === 'categories' && <CategoryMappingView session={s} onUpdate={updated => { setS(updated); onUpdate(updated); }} toast={toast} />}
        {tab === 'matching' && <MatchingView session={s} onUpdate={updated => { setS(updated); onUpdate(updated); }} selectedMatch={selectedMatch} onSelectMatch={setSelectedMatch} toast={toast} />}
        {tab === 'manual' && <ManualMatchView session={s} onUpdate={updated => { setS(updated); onUpdate(updated); }} toast={toast} />}
        {tab === 'company' && <LedgerView ledger={s.companyLedger} title="Company Ledger" matchResult={mr} side="company" />}
        {tab === 'supplier' && <LedgerView ledger={s.supplierLedger} title="Supplier Ledger" matchResult={mr} side="supplier" />}
        {tab === 'exceptions' && <ExceptionsView session={s} />}
        {tab === 'duplicates' && <DuplicatesView session={s} toast={toast} />}
        {tab === 'aging' && <AgingView session={s} />}
        {tab === 'statement' && <StatementView session={s} onUpdate={updated => { setS(updated); onUpdate(updated); }} />}
        {tab === 'actions' && <ActionsView session={s} onUpdate={updated => { setS(updated); onUpdate(updated); }} />}
        {tab === 'notes' && <NotesView session={s} onUpdate={updated => { setS(updated); onUpdate(updated); }} onSave={save} />}
      </div>
    </div>
  );
}

// ── Summary View ────────────────────────
function SummaryView({ session }) {
  const mr = session.matchResult;
  if (!mr) return <div className="empty-state"><div className="empty-icon">📊</div><div className="empty-title">No matching results yet</div><div className="empty-desc">Click "Run Matching" to analyze transactions.</div></div>;
  const { summary } = mr;
  return (
    <div>
      <h3 style={{ marginBottom: 16 }}>Matching Summary</h3>
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
        <div className="stat-card green"><div className="stat-label">Auto-Match Rate</div><div className="stat-value">{summary.autoMatchRate}%</div></div>
        <div className="stat-card"><div className="stat-label">Match Groups</div><div className="stat-value">{mr.matchGroups.length}</div></div>
        <div className="stat-card"><div className="stat-label">Exceptions</div><div className="stat-value">{mr.exceptions.length}</div></div>
      </div>
      <div className="side-by-side">
        <div><h4>Match Types</h4>
          {['1:1', '1:N', 'N:1', 'N:M'].map(type => {
            const count = mr.matchGroups.filter(g => g.matchType === type).length;
            return count > 0 ? <div key={type} className="flex-between" style={{ padding: '6px 0' }}><span>{type}</span><strong>{count}</strong></div> : null;
          })}
        </div>
        <div><h4>Rules Used</h4>
          {[...new Set(mr.matchGroups.map(g => g.rule))].map(rule => {
            const count = mr.matchGroups.filter(g => g.rule === rule).length;
            return <div key={rule} className="flex-between" style={{ padding: '6px 0' }}><span>{rule}</span><strong>{count}</strong></div>;
          })}
        </div>
      </div>
    </div>
  );
}

// ── Matching View ───────────────────────
function MatchingView({ session, onUpdate, selectedMatch, onSelectMatch, toast }) {
  const mr = session.matchResult;
  const [filter, setFilter] = useState('all');
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  if (!mr) return <div className="empty-state"><div className="empty-icon">🔗</div><div className="empty-title">Run matching first</div></div>;

  const groups = mr.matchGroups.filter(g => filter === 'all' || g.status === filter);
  const compMap = Object.fromEntries(session.companyLedger.map(t => [t.id, t]));
  const suppMap = Object.fromEntries(session.supplierLedger.map(t => [t.id, t]));

  const confirmMatch = (groupIds) => {
    const ids = Array.isArray(groupIds) ? groupIds : [groupIds];
    const pSet = new Set(ids);
    const updated = { ...session };
    updated.matchResult = { ...mr, matchGroups: mr.matchGroups.map(g => pSet.has(g.id) ? { ...g, status: 'CONFIRMED' } : g) };
    onUpdate(updated); toast(`Confirmed ${ids.length} match(es)`);
    setSelectedGroups(new Set());
  };

  const rejectMatch = (groupIds) => {
    const ids = Array.isArray(groupIds) ? groupIds : [groupIds];
    const pSet = new Set(ids);
    const updated = { ...session };
    updated.matchResult = { ...mr, matchGroups: mr.matchGroups.map(g => pSet.has(g.id) ? { ...g, status: 'REJECTED' } : g) };
    onUpdate(updated); toast(`Rejected ${ids.length} match(es)`);
    setSelectedGroups(new Set());
  };

  const unmatchGroup = (groupId) => {
    const g = mr.matchGroups.find(m => m.id === groupId);
    if (!g) return;
    const updated = { ...session };
    // Remove this group entirely
    updated.matchResult = {
      ...mr,
      matchGroups: mr.matchGroups.filter(m => m.id !== groupId),
      matchedCompanyIds: (mr.matchedCompanyIds || []).filter(id => !g.companyItems.includes(id)),
      matchedSupplierIds: (mr.matchedSupplierIds || []).filter(id => !g.supplierItems.includes(id)),
    };
    // Reset match status on freed transactions
    const freedComp = new Set(g.companyItems);
    const freedSupp = new Set(g.supplierItems);
    updated.companyLedger = session.companyLedger.map(t => freedComp.has(t.id) ? { ...t, matchStatus: 'UNMATCHED' } : t);
    updated.supplierLedger = session.supplierLedger.map(t => freedSupp.has(t.id) ? { ...t, matchStatus: 'UNMATCHED' } : t);
    // Recalculate summary
    const allCompMatched = new Set(updated.matchResult.matchedCompanyIds);
    const allSuppMatched = new Set(updated.matchResult.matchedSupplierIds);
    updated.matchResult.summary = {
      ...updated.matchResult.summary,
      matched: updated.matchResult.matchGroups.filter(g2 => g2.status === 'CONFIRMED').length,
      suggested: updated.matchResult.matchGroups.filter(g2 => g2.status === 'SUGGESTED').length,
      unmatchedCompany: session.companyLedger.length - allCompMatched.size,
      unmatchedSupplier: session.supplierLedger.length - allSuppMatched.size,
      autoMatchRate: session.companyLedger.length > 0 ? Math.round((allCompMatched.size / session.companyLedger.length) * 100) : 0,
    };
    onUpdate(updated);
    toast('Match removed - items returned to unmatched');
  };

  const toggleSelectAll = () => {
    if (selectedGroups.size === groups.length && groups.length > 0) setSelectedGroups(new Set());
    else setSelectedGroups(new Set(groups.map(g => g.id)));
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    const next = new Set(selectedGroups);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedGroups(next);
  };

  return (
    <div>
      <div className="flex-between mb-2">
        <div className="flex gap-1" style={{ alignItems: 'center' }}>
          <h3>Match Groups ({groups.length})</h3>
          {selectedGroups.size > 0 && (
            <div className="flex gap-1" style={{ marginLeft: 16 }}>
              <button className="btn btn-success btn-xs" onClick={() => confirmMatch(Array.from(selectedGroups))}>✓ Confirm {selectedGroups.size}</button>
              <button className="btn btn-danger btn-xs" onClick={() => rejectMatch(Array.from(selectedGroups))}>✕ Reject {selectedGroups.size}</button>
            </div>
          )}
        </div>
        <div className="flex gap-1">
          {['all', 'CONFIRMED', 'SUGGESTED', 'REJECTED'].map(f => (
            <button key={f} className={`btn btn-xs ${filter === f ? 'btn-teal' : 'btn-outline'}`} onClick={() => { setFilter(f); setSelectedGroups(new Set()); }}>{f === 'all' ? 'All' : f}</button>
          ))}
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 30 }}><input type="checkbox" checked={selectedGroups.size === groups.length && groups.length > 0} onChange={toggleSelectAll} /></th>
              <th>Type</th><th>Company Item(s)</th><th>Company Amt</th><th>Supplier Item(s)</th><th>Supplier Amt</th><th>Diff</th><th>Rule</th><th>Confidence</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(g => {
              const compItems = g.companyItems.map(id => compMap[id]).filter(Boolean);
              const suppItems = g.supplierItems.map(id => suppMap[id]).filter(Boolean);
              return (
                <tr key={g.id} className={g.status === 'CONFIRMED' ? 'row-matched' : g.status === 'SUGGESTED' ? 'row-suggested' : ''} onClick={() => onSelectMatch(g.id === selectedMatch ? null : g.id)} style={{ cursor: 'pointer', background: selectedGroups.has(g.id) ? 'rgba(13,148,136,0.08)' : undefined }}>
                  <td onClick={e => e.stopPropagation()}><input type="checkbox" checked={selectedGroups.has(g.id)} onChange={e => toggleSelect(g.id, e)} /></td>
                  <td><span className="badge badge-info">{g.matchType}</span></td>
                  <td>{compItems.map(t => t.docNo).join(', ')}</td>
                  <td className="currency">{formatCurrency(g.companyTotal)}</td>
                  <td>{suppItems.map(t => t.docNo).join(', ')}</td>
                  <td className="currency">{formatCurrency(g.supplierTotal)}</td>
                  <td className="currency" style={{ color: g.amountDiff > 0.01 ? 'var(--red)' : 'var(--green)' }}>{formatCurrency(g.amountDiff)}</td>
                  <td><span className="badge badge-neutral">{g.rule}</span></td>
                  <td><ConfidenceBadge value={g.confidence} /></td>
                  <td><span className={`badge badge-${g.status === 'CONFIRMED' ? 'success' : g.status === 'SUGGESTED' ? 'warning' : 'danger'}`}>{g.status}</span></td>
                  <td>
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                      {g.status === 'SUGGESTED' && (
                        <>
                          <button className="btn btn-success btn-xs" onClick={() => confirmMatch(g.id)}>✓</button>
                          <button className="btn btn-danger btn-xs" onClick={() => rejectMatch(g.id)}>✕</button>
                        </>
                      )}
                      {g.status === 'CONFIRMED' && (
                        <button className="btn btn-outline btn-xs" onClick={() => { if (window.confirm('Unmatch this group?')) unmatchGroup(g.id); }} title="Unmatch">↩</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedMatch && (() => {
        const g = mr.matchGroups.find(m => m.id === selectedMatch);
        if (!g || !g.scoreDetail) return null;
        const sd = g.scoreDetail;
        return (
          <div className="card mt-2" style={{ background: 'var(--bg)' }}>
            <h4 style={{ marginBottom: 12 }}>Match Score Breakdown</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8 }}>
              <ScoreBar label="Doc No" score={Math.round((sd.scores?.docNo || 0) * 100)} />
              <ScoreBar label="Amount" score={Math.round((sd.scores?.amount || 0) * 100)} />
              <ScoreBar label="Date" score={Math.round((sd.scores?.date || 0) * 100)} />
              <ScoreBar label="Narration" score={Math.round((sd.scores?.narration || 0) * 100)} />
              <ScoreBar label="Tx Type" score={Math.round((sd.scores?.txType || 0) * 100)} />
              <ScoreBar label="Invoice Ref" score={Math.round((sd.scores?.invoiceRef || 0) * 100)} />
            </div>
            <div className="mt-1" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Amount difference: {formatCurrency(g.amountDiff)} • Date difference: {g.dateDiff} days • Rule: {g.rule}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Manual Matching View ────────────────
function ManualMatchView({ session, onUpdate, toast }) {
  const [selComp, setSelComp] = useState(new Set());
  const [selSupp, setSelSupp] = useState(new Set());
  const [searchComp, setSearchComp] = useState('');
  const [searchSupp, setSearchSupp] = useState('');
  const [expandedComp, setExpandedComp] = useState(new Set());
  const [expandedSupp, setExpandedSupp] = useState(new Set());
  const [groupComp, setGroupComp] = useState(true);
  const [groupSupp, setGroupSupp] = useState(true);

  const unmatchedComp = session.companyLedger.filter(t => t.matchStatus === 'UNMATCHED' || !t.matchStatus);
  const unmatchedSupp = session.supplierLedger.filter(t => t.matchStatus === 'UNMATCHED' || !t.matchStatus);

  const filtComp = unmatchedComp.filter(t => !searchComp || t.docNo?.toLowerCase().includes(searchComp.toLowerCase()) || t.narration?.toLowerCase().includes(searchComp.toLowerCase()));
  const filtSupp = unmatchedSupp.filter(t => !searchSupp || t.docNo?.toLowerCase().includes(searchSupp.toLowerCase()) || t.narration?.toLowerCase().includes(searchSupp.toLowerCase()));

  // Group multi-line entries (BPN/BPS/BR/JW) by doc no for display
  const groupedComp = groupComp ? groupTxByDoc(filtComp) : filtComp.map(t => ({ ...t, _children: null }));
  const groupedSupp = groupSupp ? groupTxByDoc(filtSupp) : filtSupp.map(t => ({ ...t, _children: null }));

  const selCompTotal = session.companyLedger.filter(t => selComp.has(t.id)).reduce((s, t) => s + (t.debit||0) - (t.credit||0), 0);
  const selSuppTotal = session.supplierLedger.filter(t => selSupp.has(t.id)).reduce((s, t) => s + (t.debit||0) - (t.credit||0), 0);

  const createManualMatch = () => {
    const compIds = Array.from(selComp);
    const suppIds = Array.from(selSupp);

    // Determine if this is a same-side adjustment (only one side selected)
    const isSameSideComp = compIds.length >= 2 && suppIds.length === 0;
    const isSameSideSupp = suppIds.length >= 2 && compIds.length === 0;
    const isSameSide = isSameSideComp || isSameSideSupp;

    if (!isSameSide && (compIds.length === 0 || suppIds.length === 0)) {
      toast('Select items from both sides, or select 2+ items from one side for same-side adjustment', 'error');
      return;
    }

    // For same-side adjustment, verify net is near zero
    if (isSameSide) {
      const side = isSameSideComp ? 'company' : 'supplier';
      const ledger = side === 'company' ? session.companyLedger : session.supplierLedger;
      const ids = side === 'company' ? compIds : suppIds;
      const selectedTxns = ledger.filter(t => ids.includes(t.id));
      const net = selectedTxns.reduce((s, t) => s + (t.debit || 0) - (t.credit || 0), 0);
      const totalAbs = selectedTxns.reduce((s, t) => s + (t.debit || 0) + (t.credit || 0), 0);

      if (Math.abs(net) > 0.01 && Math.abs(net) > totalAbs * 0.01) {
        const proceed = window.confirm(
          `The selected ${ids.length} items have a net balance of ${formatCurrency(Math.abs(net))} ${net > 0 ? 'DR' : 'CR'} (not zero).\n\nAre you sure you want to adjust these as same-side entries?`
        );
        if (!proceed) return;
      }
    }

    const compAmt = Math.abs(session.companyLedger.filter(t => selComp.has(t.id)).reduce((s, t) => s + (t.debit||0) + (t.credit||0), 0));
    const suppAmt = Math.abs(session.supplierLedger.filter(t => selSupp.has(t.id)).reduce((s, t) => s + (t.debit||0) + (t.credit||0), 0));

    const newGroup = {
      id: (isSameSide ? '_adj' : '_mg') + Math.random().toString(36).substr(2,9),
      matchType: isSameSide ? 'ADJ' : (compIds.length === 1 && suppIds.length === 1 ? '1:1' : compIds.length === 1 ? '1:N' : suppIds.length === 1 ? 'N:1' : 'N:M'),
      companyItems: compIds, supplierItems: suppIds,
      confidence: 100,
      rule: isSameSide ? `Same-Side Adjustment (${isSameSideComp ? 'Company' : 'Supplier'})` : 'Manual Match',
      status: 'CONFIRMED',
      scoreDetail: { composite: 1.0, scores: { manual: 1.0 } },
      amountDiff: isSameSide ? 0 : Math.round(Math.abs(compAmt - suppAmt)*100)/100,
      dateDiff: 0, companyTotal: compAmt, supplierTotal: suppAmt,
      isAdjustment: isSameSide || undefined,
    };

    const mr = session.matchResult || { matchGroups: [], exceptions: [], summary: { totalCompany: session.companyLedger.length, totalSupplier: session.supplierLedger.length, matched: 0, suggested: 0, unmatchedCompany: 0, unmatchedSupplier: 0, autoMatchRate: 0 }, matchedCompanyIds: [], matchedSupplierIds: [] };
    const updatedMr = { ...mr, matchGroups: [...mr.matchGroups, newGroup], matchedCompanyIds: [...(mr.matchedCompanyIds || []), ...compIds], matchedSupplierIds: [...(mr.matchedSupplierIds || []), ...suppIds] };

    // Recalculate summary
    const allCompMatched = new Set(updatedMr.matchedCompanyIds);
    const allSuppMatched = new Set(updatedMr.matchedSupplierIds);
    updatedMr.summary = {
      ...updatedMr.summary,
      matched: updatedMr.matchGroups.filter(g => g.status === 'CONFIRMED').length,
      suggested: updatedMr.matchGroups.filter(g => g.status === 'SUGGESTED').length,
      unmatchedCompany: session.companyLedger.length - allCompMatched.size,
      unmatchedSupplier: session.supplierLedger.length - allSuppMatched.size,
      autoMatchRate: session.companyLedger.length > 0 ? Math.round((allCompMatched.size / session.companyLedger.length) * 100) : 0,
    };

    const updated = { ...session, matchResult: updatedMr };
    updated.companyLedger = session.companyLedger.map(t => ({ ...t, matchStatus: allCompMatched.has(t.id) ? 'CONFIRMED' : (t.matchStatus === 'CONFIRMED' ? 'CONFIRMED' : 'UNMATCHED') }));
    updated.supplierLedger = session.supplierLedger.map(t => ({ ...t, matchStatus: allSuppMatched.has(t.id) ? 'CONFIRMED' : (t.matchStatus === 'CONFIRMED' ? 'CONFIRMED' : 'UNMATCHED') }));

    onUpdate(updated);
    setSelComp(new Set()); setSelSupp(new Set());
    if (isSameSide) toast(`Adjusted ${compIds.length + suppIds.length} items within ${isSameSideComp ? 'Company' : 'Supplier'} ledger (same-side adjustment)`);
    else toast(`Manually matched ${compIds.length} company + ${suppIds.length} supplier items`);
  };

  // Toggle selection — for grouped items, select/deselect ALL underlying children
  const toggleComp = (item) => {
    const n = new Set(selComp);
    if (item._children && item._children.length > 1) {
      const allChildIds = item._children.map(c => c.id);
      const allSelected = allChildIds.every(id => n.has(id));
      if (allSelected) allChildIds.forEach(id => n.delete(id));
      else allChildIds.forEach(id => n.add(id));
    } else {
      const realId = item._children ? item._children[0].id : item.id;
      if (n.has(realId)) n.delete(realId); else n.add(realId);
    }
    setSelComp(n);
  };
  const toggleCompChild = (id) => { const n = new Set(selComp); if (n.has(id)) n.delete(id); else n.add(id); setSelComp(n); };

  const toggleSupp = (item) => {
    const n = new Set(selSupp);
    if (item._children && item._children.length > 1) {
      const allChildIds = item._children.map(c => c.id);
      const allSelected = allChildIds.every(id => n.has(id));
      if (allSelected) allChildIds.forEach(id => n.delete(id));
      else allChildIds.forEach(id => n.add(id));
    } else {
      const realId = item._children ? item._children[0].id : item.id;
      if (n.has(realId)) n.delete(realId); else n.add(realId);
    }
    setSelSupp(n);
  };
  const toggleSuppChild = (id) => { const n = new Set(selSupp); if (n.has(id)) n.delete(id); else n.add(id); setSelSupp(n); };

  // Check if group is fully/partially selected
  const isGroupSelected = (item, selSet) => {
    if (item._children && item._children.length > 1) return item._children.every(c => selSet.has(c.id));
    return selSet.has(item._children ? item._children[0].id : item.id);
  };
  const isGroupPartial = (item, selSet) => {
    if (item._children && item._children.length > 1) return item._children.some(c => selSet.has(c.id)) && !item._children.every(c => selSet.has(c.id));
    return false;
  };

  // ── Same-side adjustment: find DR↔CR reversal pairs within one ledger ──
  const adjustReversals = (side) => {
    const ledger = side === 'company' ? session.companyLedger : session.supplierLedger;
    const unmatched = ledger.filter(t => t.matchStatus === 'UNMATCHED' || !t.matchStatus);

    // Build index: absolute amount → [{id, net}]
    const drItems = []; // net > 0
    const crItems = []; // net < 0
    unmatched.forEach(t => {
      const net = (t.debit || 0) - (t.credit || 0);
      if (net > 0.01) drItems.push({ id: t.id, amount: Math.round(net * 100) / 100, t });
      else if (net < -0.01) crItems.push({ id: t.id, amount: Math.round(Math.abs(net) * 100) / 100, t });
    });

    // Find pairs with same absolute amount
    const crAmtMap = {};
    crItems.forEach(c => {
      const key = c.amount.toFixed(2);
      if (!crAmtMap[key]) crAmtMap[key] = [];
      crAmtMap[key].push(c);
    });

    const usedCr = new Set();
    const pairs = [];
    drItems.forEach(dr => {
      const key = dr.amount.toFixed(2);
      const candidates = crAmtMap[key];
      if (!candidates) return;
      const cr = candidates.find(c => !usedCr.has(c.id));
      if (!cr) return;
      usedCr.add(cr.id);
      pairs.push({ drId: dr.id, crId: cr.id, amount: dr.amount });
    });

    if (pairs.length === 0) {
      toast(`No reversal pairs found in ${side} ledger`, 'info');
      return;
    }

    // Mark both items in each pair as ADJUSTED
    const adjustedIds = new Set();
    pairs.forEach(p => { adjustedIds.add(p.drId); adjustedIds.add(p.crId); });

    const mr = session.matchResult || { matchGroups: [], exceptions: [], summary: { totalCompany: session.companyLedger.length, totalSupplier: session.supplierLedger.length, matched: 0, suggested: 0, unmatchedCompany: 0, unmatchedSupplier: 0, autoMatchRate: 0 }, matchedCompanyIds: [], matchedSupplierIds: [] };

    // Create adjustment groups (same-side, no cross-matching)
    const newGroups = pairs.map(p => ({
      id: '_adj' + Math.random().toString(36).substr(2, 9),
      matchType: 'ADJ',
      companyItems: side === 'company' ? [p.drId, p.crId] : [],
      supplierItems: side === 'supplier' ? [p.drId, p.crId] : [],
      confidence: 100, rule: `Same-Side Reversal (${side})`, status: 'CONFIRMED',
      scoreDetail: { composite: 1.0, scores: { reversal: 1.0 } },
      amountDiff: 0, dateDiff: 0,
      companyTotal: side === 'company' ? p.amount : 0,
      supplierTotal: side === 'supplier' ? p.amount : 0,
      isAdjustment: true,
    }));

    const newCompIds = side === 'company' ? Array.from(adjustedIds) : [];
    const newSuppIds = side === 'supplier' ? Array.from(adjustedIds) : [];
    const updatedMr = {
      ...mr,
      matchGroups: [...mr.matchGroups, ...newGroups],
      matchedCompanyIds: [...(mr.matchedCompanyIds || []), ...newCompIds],
      matchedSupplierIds: [...(mr.matchedSupplierIds || []), ...newSuppIds],
    };

    const allCompMatched = new Set(updatedMr.matchedCompanyIds);
    const allSuppMatched = new Set(updatedMr.matchedSupplierIds);
    updatedMr.summary = {
      ...updatedMr.summary,
      matched: updatedMr.matchGroups.filter(g => g.status === 'CONFIRMED').length,
      suggested: updatedMr.matchGroups.filter(g => g.status === 'SUGGESTED').length,
      unmatchedCompany: session.companyLedger.length - allCompMatched.size,
      unmatchedSupplier: session.supplierLedger.length - allSuppMatched.size,
      autoMatchRate: session.companyLedger.length > 0 ? Math.round((allCompMatched.size / session.companyLedger.length) * 100) : 0,
    };

    const updated = { ...session, matchResult: updatedMr };
    updated.companyLedger = session.companyLedger.map(t => ({
      ...t, matchStatus: allCompMatched.has(t.id) ? 'CONFIRMED' : (t.matchStatus === 'CONFIRMED' ? 'CONFIRMED' : 'UNMATCHED')
    }));
    updated.supplierLedger = session.supplierLedger.map(t => ({
      ...t, matchStatus: allSuppMatched.has(t.id) ? 'CONFIRMED' : (t.matchStatus === 'CONFIRMED' ? 'CONFIRMED' : 'UNMATCHED')
    }));

    onUpdate(updated);
    toast(`Adjusted ${pairs.length} reversal pair(s) in ${side} ledger (${adjustedIds.size} items, net impact ₹0)`, 'success');
  };

  const toggleExpand = (setFn, curSet, key) => {
    const n = new Set(curSet);
    if (n.has(key)) n.delete(key); else n.add(key);
    setFn(n);
  };

  // ── Auto-match by exact amount: find all company↔supplier pairs with same NET absolute amount ──
  const autoMatchByAmount = () => {
    // Build amount index for supplier grouped items
    const suppAmtMap = {}; // key: rounded abs net → [item]
    groupedSupp.forEach(item => {
      const net = Math.abs(Math.round(((item.debit||0) - (item.credit||0)) * 100) / 100);
      if (net < 0.01) return;
      const key = net.toFixed(2);
      if (!suppAmtMap[key]) suppAmtMap[key] = [];
      suppAmtMap[key].push(item);
    });

    const newSelComp = new Set(selComp);
    const newSelSupp = new Set(selSupp);
    let pairsFound = 0;
    const usedSuppKeys = new Set(); // track used supplier items to avoid double-matching

    groupedComp.forEach(compItem => {
      const compNet = Math.abs(Math.round(((compItem.debit||0) - (compItem.credit||0)) * 100) / 100);
      if (compNet < 0.01) return;
      const key = compNet.toFixed(2);
      const candidates = suppAmtMap[key];
      if (!candidates) return;

      // Find first unused supplier match
      const suppItem = candidates.find(si => {
        const sid = si._children && si._children.length > 1 ? si._children.map(c=>c.id).join(',') : (si._children ? si._children[0].id : si.id);
        return !usedSuppKeys.has(sid);
      });
      if (!suppItem) return;

      // Mark supplier as used
      const suppKey = suppItem._children && suppItem._children.length > 1 ? suppItem._children.map(c=>c.id).join(',') : (suppItem._children ? suppItem._children[0].id : suppItem.id);
      usedSuppKeys.add(suppKey);

      // Select company items
      if (compItem._children && compItem._children.length > 1) {
        compItem._children.forEach(c => newSelComp.add(c.id));
      } else {
        newSelComp.add(compItem._children ? compItem._children[0].id : compItem.id);
      }
      // Select supplier items
      if (suppItem._children && suppItem._children.length > 1) {
        suppItem._children.forEach(c => newSelSupp.add(c.id));
      } else {
        newSelSupp.add(suppItem._children ? suppItem._children[0].id : suppItem.id);
      }
      pairsFound++;
    });

    setSelComp(newSelComp);
    setSelSupp(newSelSupp);
    if (pairsFound > 0) toast(`Found ${pairsFound} exact amount match(es) — review and click "Link Selected" to confirm`, 'success');
    else toast('No exact amount matches found in remaining unmatched items', 'info');
  };

  // ── Batch link all selected amount-matched pairs at once ──
  const batchLinkByAmount = () => {
    // Build amount index for supplier grouped items
    const suppAmtMap = {};
    groupedSupp.forEach(item => {
      const net = Math.abs(Math.round(((item.debit||0) - (item.credit||0)) * 100) / 100);
      if (net < 0.01) return;
      const key = net.toFixed(2);
      if (!suppAmtMap[key]) suppAmtMap[key] = [];
      suppAmtMap[key].push(item);
    });

    const usedSuppKeys = new Set();
    const pairs = []; // [{compIds: [], suppIds: []}]

    groupedComp.forEach(compItem => {
      const compNet = Math.abs(Math.round(((compItem.debit||0) - (compItem.credit||0)) * 100) / 100);
      if (compNet < 0.01) return;
      const key = compNet.toFixed(2);
      const candidates = suppAmtMap[key];
      if (!candidates) return;

      const suppItem = candidates.find(si => {
        const sid = si._children && si._children.length > 1 ? si._children.map(c=>c.id).join(',') : (si._children ? si._children[0].id : si.id);
        return !usedSuppKeys.has(sid);
      });
      if (!suppItem) return;

      const suppKeyId = suppItem._children && suppItem._children.length > 1 ? suppItem._children.map(c=>c.id).join(',') : (suppItem._children ? suppItem._children[0].id : suppItem.id);
      usedSuppKeys.add(suppKeyId);

      const compIds = compItem._children && compItem._children.length > 1 ? compItem._children.map(c=>c.id) : [compItem._children ? compItem._children[0].id : compItem.id];
      const suppIds = suppItem._children && suppItem._children.length > 1 ? suppItem._children.map(c=>c.id) : [suppItem._children ? suppItem._children[0].id : suppItem.id];
      pairs.push({ compIds, suppIds, amount: compNet });
    });

    if (pairs.length === 0) { toast('No exact amount matches found', 'info'); return; }

    // Create match groups for all pairs
    const mr = session.matchResult || { matchGroups: [], exceptions: [], summary: { totalCompany: session.companyLedger.length, totalSupplier: session.supplierLedger.length, matched: 0, suggested: 0, unmatchedCompany: 0, unmatchedSupplier: 0, autoMatchRate: 0 }, matchedCompanyIds: [], matchedSupplierIds: [] };
    const newGroups = pairs.map(p => ({
      id: '_mg' + Math.random().toString(36).substr(2,9),
      matchType: p.compIds.length === 1 && p.suppIds.length === 1 ? '1:1' : p.compIds.length === 1 ? '1:N' : p.suppIds.length === 1 ? 'N:1' : 'N:M',
      companyItems: p.compIds, supplierItems: p.suppIds,
      confidence: 95, rule: 'Amount Match (Manual Batch)', status: 'CONFIRMED',
      scoreDetail: { composite: 0.95, scores: { amount: 1.0 } },
      amountDiff: 0, dateDiff: 0, companyTotal: p.amount, supplierTotal: p.amount,
    }));

    const allNewCompIds = pairs.flatMap(p => p.compIds);
    const allNewSuppIds = pairs.flatMap(p => p.suppIds);
    const updatedMr = { ...mr, matchGroups: [...mr.matchGroups, ...newGroups], matchedCompanyIds: [...(mr.matchedCompanyIds || []), ...allNewCompIds], matchedSupplierIds: [...(mr.matchedSupplierIds || []), ...allNewSuppIds] };

    const allCompMatched = new Set(updatedMr.matchedCompanyIds);
    const allSuppMatched = new Set(updatedMr.matchedSupplierIds);
    updatedMr.summary = {
      ...updatedMr.summary,
      matched: updatedMr.matchGroups.filter(g => g.status === 'CONFIRMED').length,
      suggested: updatedMr.matchGroups.filter(g => g.status === 'SUGGESTED').length,
      unmatchedCompany: session.companyLedger.length - allCompMatched.size,
      unmatchedSupplier: session.supplierLedger.length - allSuppMatched.size,
      autoMatchRate: session.companyLedger.length > 0 ? Math.round((allCompMatched.size / session.companyLedger.length) * 100) : 0,
    };

    const updated = { ...session, matchResult: updatedMr };
    updated.companyLedger = session.companyLedger.map(t => ({ ...t, matchStatus: allCompMatched.has(t.id) ? 'CONFIRMED' : (t.matchStatus === 'CONFIRMED' ? 'CONFIRMED' : 'UNMATCHED') }));
    updated.supplierLedger = session.supplierLedger.map(t => ({ ...t, matchStatus: allSuppMatched.has(t.id) ? 'CONFIRMED' : (t.matchStatus === 'CONFIRMED' ? 'CONFIRMED' : 'UNMATCHED') }));

    onUpdate(updated);
    setSelComp(new Set()); setSelSupp(new Set());
    toast(`Batch matched ${pairs.length} pair(s) by exact amount`, 'success');
  };

  // Render a side's table (company or supplier)
  const renderSideTable = (grouped, selSet, toggleItem, toggleChild, expandedSet, setExpanded, isComp) => (
    <table style={{ fontSize: 12 }}>
      <thead><tr><th style={{width:30}}></th><th>Date</th><th>Doc</th><th>Net Amt</th><th>Type</th></tr></thead>
      <tbody>
        {grouped.slice(0, 200).map(item => {
          const isGrp = item._children && item._children.length > 1;
          const key = item.id;
          const isExp = expandedSet.has(key);
          const net = (item.debit||0) - (item.credit||0);
          const selected = isGroupSelected(item, selSet);
          const partial = isGroupPartial(item, selSet);
          return (
            <React.Fragment key={key}>
              <tr style={{ background: selected ? 'rgba(13,148,136,0.12)' : partial ? 'rgba(13,148,136,0.06)' : '', cursor: 'pointer' }}>
                <td onClick={() => toggleItem(item)}>
                  <input type="checkbox" checked={selected} ref={el => { if (el) el.indeterminate = partial; }} onChange={() => toggleItem(item)} />
                </td>
                <td onClick={() => toggleItem(item)}>{formatDate(item.date)}</td>
                <td>
                  {isGrp && <span style={{ cursor: 'pointer', fontSize: 10, color: 'var(--teal)', marginRight: 4 }} onClick={(e) => { e.stopPropagation(); toggleExpand(setExpanded, expandedSet, key); }}>{isExp ? '▼' : '▶'}</span>}
                  <strong onClick={() => toggleItem(item)}>{item.docNo}</strong>
                  {isGrp && <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 4 }}>({item._children.length} lines)</span>}
                </td>
                <td className="currency" onClick={() => toggleItem(item)} style={{ fontWeight: isGrp ? 700 : 400, color: isGrp ? 'var(--teal)' : '' }}>
                  {formatCurrency(Math.abs(net))} <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{net >= 0 ? 'DR' : 'CR'}</span>
                </td>
                <td onClick={() => toggleItem(item)}><span className="badge badge-neutral">{item.type}</span></td>
              </tr>
              {isGrp && isExp && item._children.map((c, ci) => {
                const cNet = (c.debit||0) - (c.credit||0);
                return (
                  <tr key={key+'_'+ci} style={{ background: selSet.has(c.id) ? 'rgba(13,148,136,0.10)' : 'rgba(200,200,200,0.08)', fontSize: 11, cursor: 'pointer' }} onClick={() => toggleChild(c.id)}>
                    <td><input type="checkbox" checked={selSet.has(c.id)} onChange={() => toggleChild(c.id)} /></td>
                    <td style={{ color: 'var(--text-muted)' }}>{formatDate(c.date)}</td>
                    <td style={{ paddingLeft: 24, color: 'var(--text-muted)' }}>↳ {c.docNo}</td>
                    <td className="currency" style={{ color: cNet >= 0 ? '' : 'var(--red)' }}>{formatCurrency(Math.abs(cNet))} <span style={{ fontSize: 9 }}>{cNet >= 0 ? 'DR' : 'CR'}</span></td>
                    <td><span className="badge badge-neutral" style={{ fontSize: 9, padding: '1px 4px' }}>{c.type}</span></td>
                  </tr>
                );
              })}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div>
      <h3 style={{ marginBottom: 16 }}>Manual Matching</h3>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>Select unmatched items from both sides and click "Link Selected" to create a manual match. Multi-line entries (BPN/BPS) are grouped by doc no showing NET amount.</p>

      {/* ── Quick Actions Bar ── */}
      <div className="card mb-2" style={{ padding: '12px 16px', background: 'var(--bg)', border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <strong style={{ fontSize: 13, color: 'var(--text-secondary)', marginRight: 4 }}>Cross-Match:</strong>
          <button className="btn btn-outline btn-sm" onClick={autoMatchByAmount} title="Find and highlight all items with exact matching amounts on both sides">
            🔍 Find Exact Amounts
          </button>
          <button className="btn btn-teal btn-sm" onClick={batchLinkByAmount} title="Automatically link all unmatched items where company and supplier NET amounts match exactly">
            ⚡ Auto-Link All Exact Amounts
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <strong style={{ fontSize: 13, color: 'var(--text-secondary)', marginRight: 4 }}>Same-Side Adjust:</strong>
          <button className="btn btn-outline btn-sm" onClick={() => adjustReversals('company')} title="Find and adjust DR↔CR reversal pairs within Company ledger (net impact = zero)" style={{ borderColor: 'var(--blue, #3b82f6)', color: 'var(--blue, #3b82f6)' }}>
            🔄 Adjust Company Reversals
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => adjustReversals('supplier')} title="Find and adjust DR↔CR reversal pairs within Supplier ledger (net impact = zero)" style={{ borderColor: 'var(--amber)', color: 'var(--amber)' }}>
            🔄 Adjust Supplier Reversals
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Pairs equal DR & CR amounts within same book (e.g. entry + reversal = net ₹0)</span>
        </div>
      </div>

      {(selComp.size > 0 || selSupp.size > 0) && (() => {
        const isSameSideComp = selComp.size >= 2 && selSupp.size === 0;
        const isSameSideSupp = selSupp.size >= 2 && selComp.size === 0;
        const isSameSide = isSameSideComp || isSameSideSupp;
        const canLink = (selComp.size > 0 && selSupp.size > 0) || isSameSide;
        const sameSideNet = isSameSideComp
          ? session.companyLedger.filter(t => selComp.has(t.id)).reduce((s, t) => s + (t.debit||0) - (t.credit||0), 0)
          : isSameSideSupp
          ? session.supplierLedger.filter(t => selSupp.has(t.id)).reduce((s, t) => s + (t.debit||0) - (t.credit||0), 0)
          : 0;

        return (
          <div className="card mb-2" style={{ background: isSameSide ? 'rgba(59,130,246,0.06)' : 'var(--bg)', padding: 16, border: isSameSide ? '2px solid var(--blue, #3b82f6)' : '' }}>
            <div className="flex-between">
              <div>
                {isSameSide ? (
                  <>
                    <span style={{ fontSize: 11, background: 'var(--blue, #3b82f6)', color: '#fff', padding: '2px 8px', borderRadius: 4, marginRight: 8 }}>SAME-SIDE ADJUST</span>
                    <strong>{isSameSideComp ? selComp.size : selSupp.size}</strong> {isSameSideComp ? 'company' : 'supplier'} items
                    <span style={{ marginLeft: 8 }}>Net: {formatCurrency(Math.abs(sameSideNet))} {sameSideNet >= 0 ? 'DR' : 'CR'}</span>
                    {Math.abs(sameSideNet) < 0.01 && <span style={{ color: 'var(--green)', marginLeft: 8, fontWeight: 600 }}>✓ Net Zero</span>}
                    {Math.abs(sameSideNet) >= 0.01 && <span style={{ color: 'var(--amber)', marginLeft: 8 }}>⚠ Non-zero net</span>}
                  </>
                ) : (
                  <>
                    <strong>{selComp.size}</strong> company items ({formatCurrency(Math.abs(selCompTotal))}) + <strong>{selSupp.size}</strong> supplier items ({formatCurrency(Math.abs(selSuppTotal))})
                    {Math.abs(Math.abs(selCompTotal) - Math.abs(selSuppTotal)) > 0.01 && <span style={{ color: 'var(--amber)', marginLeft: 8 }}>Diff: {formatCurrency(Math.abs(Math.abs(selCompTotal) - Math.abs(selSuppTotal)))}</span>}
                  </>
                )}
              </div>
              <div className="flex gap-1">
                <button className="btn btn-success btn-sm" onClick={createManualMatch} disabled={!canLink}>
                  {isSameSide ? '🔄 Adjust Selected' : 'Link Selected'}
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => { setSelComp(new Set()); setSelSupp(new Set()); }}>Clear</button>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="side-by-side">
        <div>
          <div className="flex-between mb-1">
            <h4>Company Unmatched ({unmatchedComp.length}){groupComp && groupedComp.length !== filtComp.length && <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}> → {groupedComp.length} groups</span>}</h4>
            <div className="flex gap-1" style={{ alignItems: 'center' }}>
              <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={groupComp} onChange={e => setGroupComp(e.target.checked)} /> Group
              </label>
              <input type="text" placeholder="Search..." value={searchComp} onChange={e => setSearchComp(e.target.value)} style={{ width: 120 }} />
            </div>
          </div>
          <div className="table-wrap" style={{ maxHeight: 400, overflowY: 'auto' }}>
            {renderSideTable(groupedComp, selComp, toggleComp, toggleCompChild, expandedComp, setExpandedComp, true)}
          </div>
        </div>
        <div>
          <div className="flex-between mb-1">
            <h4>Supplier Unmatched ({unmatchedSupp.length}){groupSupp && groupedSupp.length !== filtSupp.length && <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}> → {groupedSupp.length} groups</span>}</h4>
            <div className="flex gap-1" style={{ alignItems: 'center' }}>
              <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={groupSupp} onChange={e => setGroupSupp(e.target.checked)} /> Group
              </label>
              <input type="text" placeholder="Search..." value={searchSupp} onChange={e => setSearchSupp(e.target.value)} style={{ width: 120 }} />
            </div>
          </div>
          <div className="table-wrap" style={{ maxHeight: 400, overflowY: 'auto' }}>
            {renderSideTable(groupedSupp, selSupp, toggleSupp, toggleSuppChild, expandedSupp, setExpandedSupp, false)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Duplicate Detection View ────────────
function DuplicatesView({ session, toast }) {
  const [dups, setDups] = useState(null);
  const [side, setSide] = useState('company');

  const runDupCheck = () => {
    const ledger = side === 'company' ? session.companyLedger : session.supplierLedger;
    const found = detectDuplicates(ledger);
    setDups(found);
    toast(`Found ${found.length} potential duplicate(s) in ${side} ledger`, found.length > 0 ? 'info' : 'success');
  };

  const ledger = side === 'company' ? session.companyLedger : session.supplierLedger;
  const txMap = Object.fromEntries(ledger.map(t => [t.id, t]));

  return (
    <div>
      <h3 style={{ marginBottom: 16 }}>Duplicate Detection</h3>
      <div className="flex gap-1 mb-2">
        <select value={side} onChange={e => { setSide(e.target.value); setDups(null); }} style={{ width: 180 }}>
          <option value="company">Company Ledger</option>
          <option value="supplier">Supplier Ledger</option>
        </select>
        <button className="btn btn-teal" onClick={runDupCheck}>Scan for Duplicates</button>
      </div>
      {dups !== null && (
        dups.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">✅</div><div className="empty-title">No duplicates found</div></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Txn A - Date</th><th>Txn A - Doc</th><th>Txn A - Amount</th><th></th><th>Txn B - Date</th><th>Txn B - Doc</th><th>Txn B - Amount</th></tr></thead>
              <tbody>
                {dups.map((d, i) => {
                  const a = txMap[d.txA], b = txMap[d.txB];
                  if (!a || !b) return null;
                  return (
                    <tr key={i} style={{ background: 'var(--red-bg)' }}>
                      <td>{formatDate(a.date)}</td><td><strong>{a.docNo}</strong></td><td className="currency">{formatCurrency((a.debit||0)+(a.credit||0))}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--red)' }}>DUP</td>
                      <td>{formatDate(b.date)}</td><td><strong>{b.docNo}</strong></td><td className="currency">{formatCurrency((b.debit||0)+(b.credit||0))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

// ── Ledger View ─────────────────────────
function LedgerView({ ledger, title, matchResult, side }) {
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const types = [...new Set(ledger.map(t => t.type))];
  const filtered = ledger.filter(t => {
    const ms = !search || t.docNo?.toLowerCase().includes(search.toLowerCase()) || t.narration?.toLowerCase().includes(search.toLowerCase());
    const mt = filterType === 'all' || t.type === filterType;
    const mst = filterStatus === 'all' || t.matchStatus === filterStatus;
    return ms && mt && mst;
  });
  const totalDr = filtered.reduce((s, t) => s + (t.debit || 0), 0);
  const totalCr = filtered.reduce((s, t) => s + (t.credit || 0), 0);

  return (
    <div>
      <div className="flex-between mb-2">
        <h3>{title} ({filtered.length} of {ledger.length})</h3>
        <div className="flex gap-1">
          <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 160 }} />
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ width: 140 }}>
            <option value="all">All Types</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 140 }}>
            <option value="all">All Status</option>
            <option value="CONFIRMED">Matched</option>
            <option value="SUGGESTED">Suggested</option>
            <option value="UNMATCHED">Unmatched</option>
          </select>
        </div>
      </div>
      <div className="table-wrap" style={{ maxHeight: 500, overflowY: 'auto' }}>
        <table>
          <thead><tr><th>Date</th><th>Doc No</th><th>Narration</th><th>Debit</th><th>Credit</th><th>Type</th><th>Status</th></tr></thead>
          <tbody>
            {filtered.map(t => (
              <tr key={t.id} className={t.matchStatus === 'CONFIRMED' ? 'row-matched' : t.matchStatus === 'SUGGESTED' ? 'row-suggested' : t.matchStatus === 'UNMATCHED' ? 'row-unmatched' : ''}>
                <td>{formatDate(t.date)}</td>
                <td><strong>{t.docNo}</strong></td>
                <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.narration}</td>
                <td className="currency">{t.debit ? formatCurrency(t.debit) : '—'}</td>
                <td className="currency">{t.credit ? formatCurrency(t.credit) : '—'}</td>
                <td><span className="badge badge-info">{t.type}</span></td>
                <td>{t.matchStatus && <span className={`badge badge-${t.matchStatus === 'CONFIRMED' ? 'success' : t.matchStatus === 'SUGGESTED' ? 'warning' : 'danger'}`}>{t.matchStatus}</span>}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: 700, background: 'var(--bg)' }}>
              <td colSpan={3}>Total</td>
              <td className="currency">{formatCurrency(totalDr)}</td>
              <td className="currency">{formatCurrency(totalCr)}</td>
              <td colSpan={2} className="currency">{formatCurrency(totalDr - totalCr)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Exceptions View ─────────────────────
function ExceptionsView({ session }) {
  const mr = session.matchResult;
  if (!mr) return <div className="empty-state"><div className="empty-icon">⚠</div><div className="empty-title">Run matching first</div></div>;
  const cats = [...new Set(mr.exceptions.map(e => e.category))];
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? mr.exceptions : mr.exceptions.filter(e => e.category === filter);

  return (
    <div>
      <div className="flex-between mb-2">
        <h3>Exceptions ({mr.exceptions.length})</h3>
        <div className="flex gap-1 flex-wrap">
          <button className={`btn btn-xs ${filter === 'all' ? 'btn-teal' : 'btn-outline'}`} onClick={() => setFilter('all')}>All</button>
          {cats.map(c => (
            <button key={c} className={`btn btn-xs ${filter === c ? 'btn-teal' : 'btn-outline'}`} onClick={() => setFilter(c)}>
              {c} ({mr.exceptions.filter(e => e.category === c).length})
            </button>
          ))}
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Side</th><th>Date</th><th>Doc No</th><th>Amount</th><th>Type</th><th>Category</th><th>Narration</th></tr></thead>
          <tbody>
            {filtered.map((exc, i) => {
              const t = exc.transaction;
              return (
                <tr key={i} className="row-unmatched">
                  <td><span className={`badge badge-${exc.ledgerSide === 'COMPANY' ? 'info' : 'warning'}`}>{exc.ledgerSide}</span></td>
                  <td>{formatDate(t.date)}</td>
                  <td><strong>{t.docNo}</strong></td>
                  <td className="currency">{formatCurrency((t.debit || 0) + (t.credit || 0))}</td>
                  <td><span className="badge badge-neutral">{t.type}</span></td>
                  <td><span className={`badge badge-${exc.category.includes('Missing') ? 'danger' : exc.category.includes('Timing') ? 'info' : 'warning'}`}>{exc.category}</span></td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.narration}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Aging View ──────────────────────────
function AgingView({ session }) {
  const refDate = session.recoDate ? new Date(session.recoDate) : new Date();
  const buckets = { '0-30': [], '30-60': [], '60-90': [], '90+': [] };
  const allUnmatched = [
    ...session.companyLedger.filter(t => t.matchStatus === 'UNMATCHED' || !t.matchStatus).map(t => ({ ...t, source: 'Company' })),
    ...session.supplierLedger.filter(t => t.matchStatus === 'UNMATCHED' || !t.matchStatus).map(t => ({ ...t, source: 'Supplier' })),
  ];
  allUnmatched.forEach(t => {
    const d = new Date(t.date);
    const days = Math.floor((refDate - d) / (1000 * 60 * 60 * 24));
    const key = days <= 30 ? '0-30' : days <= 60 ? '30-60' : days <= 90 ? '60-90' : '90+';
    buckets[key].push({ ...t, days });
  });
  const colors = { '0-30': '#22c55e', '30-60': '#eab308', '60-90': '#f97316', '90+': '#ef4444' };
  const totals = Object.fromEntries(Object.entries(buckets).map(([k, items]) => [k, items.reduce((s, t) => s + (t.debit || 0) + (t.credit || 0), 0)]));
  const grand = Object.values(totals).reduce((a, b) => a + b, 0) || 1;
  const [expanded, setExpanded] = useState(null);

  return (
    <div>
      <h3 style={{ marginBottom: 16 }}>Aging Analysis – Unreconciled Items</h3>
      <div className="aging-grid">
        {Object.entries(buckets).map(([key, items]) => (
          <div key={key} className={`aging-card ${expanded === key ? 'selected' : ''}`} style={{ background: colors[key] + '15' }} onClick={() => setExpanded(expanded === key ? null : key)}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{key} Days</div>
            <div className="aging-count" style={{ color: colors[key] }}>{items.length}</div>
            <div className="aging-amount">{formatCurrency(totals[key])}</div>
            <div className="aging-progress"><div className="aging-progress-fill" style={{ width: `${(totals[key] / grand) * 100}%`, background: colors[key] }} /></div>
          </div>
        ))}
      </div>
      {expanded && buckets[expanded].length > 0 && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Source</th><th>Date</th><th>Doc No</th><th>Days</th><th>Amount</th><th>Type</th></tr></thead>
            <tbody>
              {buckets[expanded].map((t, i) => (
                <tr key={i}>
                  <td><span className={`badge badge-${t.source === 'Company' ? 'info' : 'warning'}`}>{t.source}</span></td>
                  <td>{formatDate(t.date)}</td><td><strong>{t.docNo}</strong></td><td>{t.days}</td>
                  <td className="currency">{formatCurrency((t.debit || 0) + (t.credit || 0))}</td>
                  <td><span className="badge badge-neutral">{t.type}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {allUnmatched.length === 0 && <div className="empty-state"><div className="empty-icon">✅</div><div className="empty-title">All items matched</div></div>}
    </div>
  );
}

// ── Group transactions by doc no (consolidate multi-line BPN/BPS/BR/JW into NET) ──
function groupTxByDoc(txns) {
  const multiLineDocPrefixes = /^(BPN|BPS|BR|JW)\d/i;
  const groups = {};
  const singles = [];
  txns.forEach(t => {
    const doc = (t.docNo || '').trim().toUpperCase();
    if (doc.length >= 3 && multiLineDocPrefixes.test(doc)) {
      if (!groups[doc]) groups[doc] = [];
      groups[doc].push(t);
    } else {
      singles.push({ ...t, _children: null });
    }
  });
  const out = [...singles];
  Object.entries(groups).forEach(([doc, items]) => {
    if (items.length === 1) {
      out.push({ ...items[0], _children: null });
      return;
    }
    let dr = 0, cr = 0;
    items.forEach(t => { dr += (t.debit || 0); cr += (t.credit || 0); });
    const net = dr - cr;
    const base = { ...items[0] };
    base._children = items;
    base.id = '_grp_' + items[0].id;
    base.narration = `[${items.length} lines grouped] ${items[0].narration || ''}`;
    if (net >= 0) { base.debit = Math.round(net * 100) / 100; base.credit = 0; }
    else { base.debit = 0; base.credit = Math.round(Math.abs(net) * 100) / 100; }
    out.push(base);
  });
  return out;
}

// ── Statement Drilldown Component ─────────
function StatementDrilldown({ session, item, allCategories, catMap }) {
  const [expandedDoc, setExpandedDoc] = useState(null);

  if (!item.categoryId) return <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '10px 15px', background: 'var(--bg)', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', margin: '0 10px 10px 10px' }}>Custom manual entries do not support drilldown.</div>;

  const cid = item.categoryId;

  // ── Special: Matched Items Differences ──
  if (cid === '_matched_diff' && session.matchResult) {
    const diffGroups = [];
    session.matchResult.matchGroups
      .filter(g => g.status === 'CONFIRMED' || g.status === 'SUGGESTED')
      .forEach(g => {
        const compTxns = session.companyLedger.filter(t => g.companyItems.includes(t.id));
        const suppTxns = session.supplierLedger.filter(t => g.supplierItems.includes(t.id));
        // Compute NET amount per side (debit - credit = positive DR net)
        const compNet = compTxns.reduce((s, t) => s + (t.debit || 0) - (t.credit || 0), 0);
        const suppNet = suppTxns.reduce((s, t) => s + (t.debit || 0) - (t.credit || 0), 0);
        // For comparison use absolute amounts — same economic event appears on opposite sides
        const compAbsAmt = Math.abs(compNet);
        const suppAbsAmt = Math.abs(suppNet);
        const diff = Math.round((compAbsAmt - suppAbsAmt) * 100) / 100;
        if (Math.abs(diff) > 0.01) {
          diffGroups.push({
            id: g.id,
            compDoc: compTxns.map(t => t.docNo).filter(Boolean).join(', '),
            suppDoc: suppTxns.map(t => t.docNo).filter(Boolean).join(', '),
            compAmt: Math.round(compAbsAmt * 100) / 100,
            suppAmt: Math.round(suppAbsAmt * 100) / 100,
            diff,
            compSide: compNet >= 0 ? 'DR' : 'CR',
            suppSide: suppNet >= 0 ? 'DR' : 'CR',
            date: compTxns[0]?.date || suppTxns[0]?.date,
          });
        }
      });
    const totalDiff = diffGroups.reduce((s, g) => s + Math.abs(g.diff), 0);
    if (diffGroups.length === 0) return <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '10px', background: 'var(--bg)', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', margin: '0 10px 10px 10px' }}>All matched items have identical amounts.</div>;
    return (
      <div className="table-wrap" style={{ maxHeight: 300, overflowY: 'auto', background: '#fff', border: '1px solid var(--border)', borderTop: 'none', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', margin: '0 10px 10px 10px' }}>
        <table style={{ fontSize: 12, margin: 0 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}><tr><th>Company Doc</th><th>Supplier Doc</th><th>Company Amt</th><th>Supplier Amt</th><th>Difference</th></tr></thead>
          <tbody>
            {diffGroups.map(g => (
              <tr key={g.id}>
                <td><strong>{g.compDoc || '—'}</strong></td>
                <td><strong>{g.suppDoc || '—'}</strong></td>
                <td className="currency">{formatCurrency(g.compAmt)} <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{g.compSide}</span></td>
                <td className="currency">{formatCurrency(g.suppAmt)} <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{g.suppSide}</span></td>
                <td className="currency" style={{ color: 'var(--red)', fontWeight: 600 }}>{formatCurrency(Math.abs(g.diff))}</td>
              </tr>
            ))}
            <tr style={{ background: 'var(--bg)', fontWeight: 700 }}>
              <td colSpan="4" style={{ textAlign: 'right' }}>Total Differences ({diffGroups.length} pairs):</td>
              <td className="currency" style={{ color: 'var(--red)' }}>{formatCurrency(totalDiff)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // ── Special: Opening Balance Difference ──
  if (cid === '_opening_diff') {
    const obSupp = session.openingBalanceSupplier || 0;
    const obComp = session.openingBalanceCompany || 0;
    return (
      <div style={{ fontSize: 12, padding: '10px 15px', background: 'var(--bg)', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', margin: '0 10px 10px 10px' }}>
        <div style={{ marginBottom: 4 }}><strong>Supplier Opening:</strong> {formatCurrency(Math.abs(obSupp))} {obSupp < 0 ? 'CR' : 'DR'}</div>
        <div style={{ marginBottom: 4 }}><strong>Company Opening:</strong> {formatCurrency(Math.abs(obComp))} {obComp < 0 ? 'CR' : 'DR'}</div>
        <div><strong>Difference:</strong> {formatCurrency(Math.abs(Math.abs(obComp) - Math.abs(obSupp)))}</div>
      </div>
    );
  }

  const targetCategory = (t, isComp) => isComp ? (t.categoryId || 'other') : (catMap[t.categoryId || 'other'] || t.categoryId || 'other');

  // Filter by side if the item specifies company-only or supplier-only
  const showComp = !item.side || item.side === 'company';
  const showSupp = !item.side || item.side === 'supplier';

  const uCompRaw = showComp ? session.companyLedger.filter(t => t.matchStatus === 'UNMATCHED' || !t.matchStatus).filter(t => targetCategory(t, true) === cid) : [];
  const uSuppRaw = showSupp ? session.supplierLedger.filter(t => t.matchStatus === 'UNMATCHED' || !t.matchStatus).filter(t => targetCategory(t, false) === cid) : [];

  // Group multi-line entries (BPN etc) by doc no → show NET per group
  const uComp = groupTxByDoc(uCompRaw);
  const uSupp = groupTxByDoc(uSuppRaw);

  if (uComp.length === 0 && uSupp.length === 0) return <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '10px', background: 'var(--bg)', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', margin: '0 10px 10px 10px' }}>No items found.</div>;

  const renderRow = (t, side, bgColor, badgeClass) => {
    const isGrouped = t._children && t._children.length > 1;
    const docKey = side + '_' + t.id;
    return (
      <React.Fragment key={docKey}>
        <tr style={{ background: bgColor, cursor: isGrouped ? 'pointer' : 'default' }} onClick={() => isGrouped && setExpandedDoc(expandedDoc === docKey ? null : docKey)}>
          <td><span className={`badge ${badgeClass}`} style={{ fontSize: 9, padding: '2px 5px' }}>{side === 'comp' ? 'COMP' : 'SUPP'}</span></td>
          <td>{formatDate(t.date)}</td>
          <td><strong>{t.docNo}</strong>{isGrouped && <span style={{ fontSize: 9, color: 'var(--teal)', marginLeft: 4 }}>({t._children.length} lines) {expandedDoc === docKey ? '▼' : '▶'}</span>}</td>
          <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{isGrouped ? `NET of ${t._children.length} entries` : t.narration}</td>
          <td className="currency" style={{ color: 'var(--text-main)', fontWeight: isGrouped ? 700 : 400 }}>{t.debit ? formatCurrency(t.debit) : '—'}</td>
          <td className="currency" style={{ color: 'var(--text-main)', fontWeight: isGrouped ? 700 : 400 }}>{t.credit ? formatCurrency(t.credit) : '—'}</td>
        </tr>
        {isGrouped && expandedDoc === docKey && t._children.map((c, ci) => (
          <tr key={docKey + '_sub_' + ci} style={{ background: 'rgba(200,200,200,0.1)', fontSize: 11 }}>
            <td></td>
            <td style={{ color: 'var(--text-muted)' }}>{formatDate(c.date)}</td>
            <td style={{ color: 'var(--text-muted)', paddingLeft: 20 }}>↳ {c.docNo}</td>
            <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{c.narration}</td>
            <td className="currency" style={{ color: 'var(--text-muted)' }}>{c.debit ? formatCurrency(c.debit) : '—'}</td>
            <td className="currency" style={{ color: 'var(--text-muted)' }}>{c.credit ? formatCurrency(c.credit) : '—'}</td>
          </tr>
        ))}
      </React.Fragment>
    );
  };

  return (
    <div className="table-wrap" style={{ maxHeight: 300, overflowY: 'auto', background: '#fff', border: '1px solid var(--border)', borderTop: 'none', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', margin: '0 10px 10px 10px' }}>
      <table style={{ fontSize: 12, margin: 0 }}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}><tr><th>Side</th><th>Date</th><th>Doc No</th><th>Narration</th><th>Debit</th><th>Credit</th></tr></thead>
        <tbody>
          {uComp.map(t => renderRow(t, 'comp', 'rgba(239,246,255,0.3)', 'badge-info'))}
          {uSupp.map(t => renderRow(t, 'supp', 'rgba(254,252,232,0.5)', 'badge-warning'))}
        </tbody>
      </table>
    </div>
  );
}

// ── Statement View ──────────────────────
function StatementView({ session, onUpdate }) {
  const allCategories = [...DEFAULT_CATEGORIES, ...(session.customCategories || [])];
  const catMap = session.categoryMapping || DEFAULT_CATEGORY_MAPPING;

  // ── Generate reconciliation items from current session state ──
  const generateItems = useCallback(() => {
    const uCompRaw = session.companyLedger.filter(t => t.matchStatus === 'UNMATCHED' || !t.matchStatus);
    const uSuppRaw = session.supplierLedger.filter(t => t.matchStatus === 'UNMATCHED' || !t.matchStatus);

    // Group multi-line entries (BPN/BPS/BR/JW) by doc no → NET per group
    // This prevents BPN sub-lines from inflating category totals
    const uComp = groupTxByDoc(uCompRaw);
    const uSupp = groupTxByDoc(uSuppRaw);
    const generated = [];

    // ── Category-wise NET difference (combine both sides into single net per category) ──
    const buckets = {};
    const addToBucket = (cid, isComp, t) => {
      if (!buckets[cid]) buckets[cid] = { cNet: 0, sNet: 0, cCount: 0, sCount: 0 };
      if (isComp) { buckets[cid].cNet += (t.debit || 0) - (t.credit || 0); buckets[cid].cCount++; }
      else { buckets[cid].sNet += (t.debit || 0) - (t.credit || 0); buckets[cid].sCount++; }
    };

    uComp.forEach(t => addToBucket(t.categoryId || 'other', true, t));
    uSupp.forEach(t => addToBucket(catMap[t.categoryId || 'other'] || t.categoryId || 'other', false, t));

    Object.keys(buckets).forEach(cid => {
      const b = buckets[cid];
      const cat = allCategories.find(c => c.id === cid) || { label: cid };

      // Show SEPARATE company and supplier items per category for drill-down clarity
      // Company DR (payments/debit notes recorded by us, not acknowledged by supplier) → LESS
      // Company CR (purchases recorded by us, not invoiced by supplier) → ADD
      if (Math.abs(b.cNet) > 0.01) {
        if (b.cNet > 0) generated.push({ id: generateId(), label: `${cat.label.toUpperCase()} - IN COMPANY ONLY (DR)`, amount: Math.round(Math.abs(b.cNet) * 100) / 100, type: 'LESS', categoryId: cid, side: 'company', count: b.cCount });
        else generated.push({ id: generateId(), label: `${cat.label.toUpperCase()} - IN COMPANY ONLY (CR)`, amount: Math.round(Math.abs(b.cNet) * 100) / 100, type: 'ADD', categoryId: cid, side: 'company', count: b.cCount });
      }
      // Supplier DR (sales/invoices in supplier, not recorded by us) → LESS
      // Supplier CR (receipts in supplier, not recorded by us) → ADD
      if (Math.abs(b.sNet) > 0.01) {
        if (b.sNet > 0) generated.push({ id: generateId(), label: `${cat.label.toUpperCase()} - IN SUPPLIER ONLY (DR)`, amount: Math.round(Math.abs(b.sNet) * 100) / 100, type: 'LESS', categoryId: cid, side: 'supplier', count: b.sCount });
        else generated.push({ id: generateId(), label: `${cat.label.toUpperCase()} - IN SUPPLIER ONLY (CR)`, amount: Math.round(Math.abs(b.sNet) * 100) / 100, type: 'ADD', categoryId: cid, side: 'supplier', count: b.sCount });
      }
    });

    // ── Opening Balance Difference ──
    const obSupp = Math.abs(session.openingBalanceSupplier || 0);
    const obComp = Math.abs(session.openingBalanceCompany || 0);
    const obDiff = obComp - obSupp;
    if (Math.abs(obDiff) > 0.01) {
      if (obDiff > 0) generated.push({ id: generateId(), label: 'OPENING BALANCE DIFFERENCE (Company > Supplier)', amount: Math.round(Math.abs(obDiff) * 100) / 100, type: 'ADD', categoryId: '_opening_diff' });
      else generated.push({ id: generateId(), label: 'OPENING BALANCE DIFFERENCE (Supplier > Company)', amount: Math.round(Math.abs(obDiff) * 100) / 100, type: 'LESS', categoryId: '_opening_diff' });
    }

    // ── Amount differences in matched items ──
    if (session.matchResult) {
      let matchedDiffAdd = 0, matchedDiffLess = 0;
      session.matchResult.matchGroups
        .filter(g => g.status === 'CONFIRMED' || g.status === 'SUGGESTED')
        .forEach(g => {
          const compTxns = session.companyLedger.filter(t => g.companyItems.includes(t.id));
          const suppTxns = session.supplierLedger.filter(t => g.supplierItems.includes(t.id));
          // NET per side (debit - credit), then compare absolute amounts
          const compNet = compTxns.reduce((s, t) => s + (t.debit || 0) - (t.credit || 0), 0);
          const suppNet = suppTxns.reduce((s, t) => s + (t.debit || 0) - (t.credit || 0), 0);
          const compAbs = Math.abs(compNet);
          const suppAbs = Math.abs(suppNet);
          const pairDiff = Math.round((compAbs - suppAbs) * 100) / 100;
          // Company amount > supplier: company has more, so supplier balance is understated → ADD
          // Company amount < supplier: supplier has more, so supplier balance is overstated → LESS
          if (pairDiff > 0.01) matchedDiffAdd += pairDiff;
          else if (pairDiff < -0.01) matchedDiffLess += Math.abs(pairDiff);
        });
      if (matchedDiffAdd > 0.01) {
        generated.push({ id: generateId(), label: 'NET AMOUNT DIFFERENCES IN MATCHED ITEMS (Company > Supplier)', amount: Math.round(matchedDiffAdd * 100) / 100, type: 'ADD', categoryId: '_matched_diff' });
      }
      if (matchedDiffLess > 0.01) {
        generated.push({ id: generateId(), label: 'NET AMOUNT DIFFERENCES IN MATCHED ITEMS (Supplier > Company)', amount: Math.round(matchedDiffLess * 100) / 100, type: 'LESS', categoryId: '_matched_diff' });
      }
    }

    return generated;
  }, [session, allCategories, catMap]);

  const [items, setItems] = useState(() => {
    if (session.recoItems && session.recoItems.length > 0) return session.recoItems;
    return generateItems();
  });

  const [editing, setEditing] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Recalculate function — regenerates items from scratch
  const recalculate = useCallback(() => {
    const newItems = generateItems();
    setItems(newItems);
    onUpdate({ ...session, recoItems: newItems });
  }, [generateItems, session, onUpdate]);

  // Net transaction movement (debit - credit)
  const suppNetMovement = session.supplierLedger.reduce((s, t) => s + (t.debit || 0) - (t.credit || 0), 0);
  const compNetMovement = session.companyLedger.reduce((s, t) => s + (t.debit || 0) - (t.credit || 0), 0);

  // Opening balances (negative = CR/payable, positive = DR/receivable)
  const suppOpenBal = session.openingBalanceSupplier || 0;
  const compOpenBal = session.openingBalanceCompany || 0;

  // CLOSING BALANCE: Use user-overridden value if set, otherwise compute from Opening + Net
  const suppClosingComputed = suppOpenBal + suppNetMovement;
  const compClosingComputed = compOpenBal + compNetMovement;
  const suppClosingBal = session.closingBalanceSupplier != null ? session.closingBalanceSupplier : suppClosingComputed;
  const compClosingBal = session.closingBalanceCompany != null ? session.closingBalanceCompany : compClosingComputed;

  const addTotal = items.filter(i => i.type === 'ADD').reduce((s, i) => s + i.amount, 0);
  const lessTotal = items.filter(i => i.type === 'LESS').reduce((s, i) => s + i.amount, 0);
  // Express both closing balances as absolute "payable" amounts
  const suppPayable = Math.abs(suppClosingBal);
  const compPayable = Math.abs(compClosingBal);
  // The calculated value: starting from supplier payable, adjust with ADD/LESS to arrive at company payable
  const calculatedRaw = Math.round((suppPayable + addTotal - lessTotal) * 100) / 100;
  const calculatedAbs = Math.abs(calculatedRaw);
  // If calculated is negative, it means adjustments flipped the balance to the opposite side
  const calculatedSide = calculatedRaw >= 0 ? (suppClosingBal <= 0 ? 'CR' : 'DR') : (suppClosingBal <= 0 ? 'DR' : 'CR');
  const variance = Math.abs(Math.round((calculatedAbs - compPayable) * 100) / 100);

  const saveItems = () => { onUpdate({ ...session, recoItems: items }); setEditing(false); };

  const [showBalSettings, setShowBalSettings] = useState(
    session.closingBalanceSupplier == null || session.closingBalanceCompany == null
  );

  const updateBal = (key, val) => {
    onUpdate({ ...session, [key]: val });
  };

  return (
    <div>
      {/* ── Balance Settings Card ── */}
      <div className="card mb-2" style={{ background: showBalSettings ? 'var(--bg)' : 'transparent', border: showBalSettings ? '2px solid var(--teal)' : '1px solid var(--border)', padding: showBalSettings ? 16 : '8px 16px' }}>
        <div className="flex-between" style={{ cursor: 'pointer' }} onClick={() => setShowBalSettings(!showBalSettings)}>
          <strong style={{ fontSize: 14, color: 'var(--teal)' }}>
            {showBalSettings ? '▼' : '▶'} Opening & Closing Balance Settings
            {(session.closingBalanceSupplier == null || session.closingBalanceCompany == null) && <span style={{ color: 'var(--red)', marginLeft: 8, fontSize: 12 }}>⚠ Enter closing balances for accurate reconciliation</span>}
          </strong>
        </div>
        {showBalSettings && (
          <div style={{ marginTop: 16 }}>
            <div className="side-by-side" style={{ gap: 24 }}>
              {/* Supplier Side */}
              <div>
                <h4 style={{ color: 'var(--teal)', marginBottom: 10, borderBottom: '2px solid var(--teal)', paddingBottom: 4 }}>Supplier Ledger</h4>
                <div className="flex gap-1 mb-1" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, minWidth: 120 }}>Opening Bal:</label>
                  <input type="number" step="0.01"
                    value={session.openingBalanceSupplier != null ? Math.abs(session.openingBalanceSupplier) : ''}
                    onChange={e => {
                      const val = parseFloat(e.target.value) || 0;
                      const sign = (session.openingBalanceSupplier || 0) <= 0 ? -1 : 1;
                      updateBal('openingBalanceSupplier', sign * val);
                    }}
                    placeholder="Opening..." style={{ width: 140 }} />
                  <select value={(session.openingBalanceSupplier || 0) <= 0 ? 'cr' : 'dr'}
                    onChange={e => {
                      const cur = Math.abs(session.openingBalanceSupplier || 0);
                      updateBal('openingBalanceSupplier', e.target.value === 'cr' ? -cur : cur);
                    }} style={{ width: 70 }}>
                    <option value="cr">CR</option><option value="dr">DR</option>
                  </select>
                </div>
                <div className="flex gap-1" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, minWidth: 120 }}>Closing Bal:</label>
                  <input type="number" step="0.01"
                    value={session.closingBalanceSupplier != null ? Math.abs(session.closingBalanceSupplier) : ''}
                    onChange={e => {
                      const val = parseFloat(e.target.value) || 0;
                      // Default sign: use computed closing sign, or DR if no data at all
                      const sign = session.closingBalanceSupplier != null
                        ? (session.closingBalanceSupplier < 0 ? -1 : 1)
                        : (suppClosingComputed < 0 ? -1 : 1);
                      updateBal('closingBalanceSupplier', sign * val);
                    }}
                    placeholder="Closing..." style={{ width: 140, borderColor: session.closingBalanceSupplier == null ? 'var(--red)' : '' }} />
                  <select value={session.closingBalanceSupplier != null ? (session.closingBalanceSupplier < 0 ? 'cr' : 'dr') : (suppClosingComputed < 0 ? 'cr' : 'dr')}
                    onChange={e => {
                      const cur = Math.abs(session.closingBalanceSupplier || 0);
                      updateBal('closingBalanceSupplier', e.target.value === 'cr' ? -cur : cur);
                    }} style={{ width: 70 }}>
                    <option value="cr">CR</option><option value="dr">DR</option>
                  </select>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  Computed: {formatCurrency(Math.abs(suppClosingComputed))} {suppClosingComputed <= 0 ? 'CR' : 'DR'}
                  <span style={{ marginLeft: 8 }}>| Net Movement: {formatCurrency(suppNetMovement)}</span>
                </div>
              </div>
              {/* Company Side */}
              <div>
                <h4 style={{ color: 'var(--blue)', marginBottom: 10, borderBottom: '2px solid var(--blue, #3b82f6)', paddingBottom: 4 }}>Company Ledger</h4>
                <div className="flex gap-1 mb-1" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, minWidth: 120 }}>Opening Bal:</label>
                  <input type="number" step="0.01"
                    value={session.openingBalanceCompany != null ? Math.abs(session.openingBalanceCompany) : ''}
                    onChange={e => {
                      const val = parseFloat(e.target.value) || 0;
                      const sign = (session.openingBalanceCompany || 0) <= 0 ? -1 : 1;
                      updateBal('openingBalanceCompany', sign * val);
                    }}
                    placeholder="Opening..." style={{ width: 140 }} />
                  <select value={(session.openingBalanceCompany || 0) <= 0 ? 'cr' : 'dr'}
                    onChange={e => {
                      const cur = Math.abs(session.openingBalanceCompany || 0);
                      updateBal('openingBalanceCompany', e.target.value === 'cr' ? -cur : cur);
                    }} style={{ width: 70 }}>
                    <option value="cr">CR</option><option value="dr">DR</option>
                  </select>
                </div>
                <div className="flex gap-1" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, minWidth: 120 }}>Closing Bal:</label>
                  <input type="number" step="0.01"
                    value={session.closingBalanceCompany != null ? Math.abs(session.closingBalanceCompany) : ''}
                    onChange={e => {
                      const val = parseFloat(e.target.value) || 0;
                      const sign = session.closingBalanceCompany != null
                        ? (session.closingBalanceCompany < 0 ? -1 : 1)
                        : (compClosingComputed < 0 ? -1 : 1);
                      updateBal('closingBalanceCompany', sign * val);
                    }}
                    placeholder="Closing..." style={{ width: 140, borderColor: session.closingBalanceCompany == null ? 'var(--red)' : '' }} />
                  <select value={session.closingBalanceCompany != null ? (session.closingBalanceCompany < 0 ? 'cr' : 'dr') : (compClosingComputed < 0 ? 'cr' : 'dr')}
                    onChange={e => {
                      const cur = Math.abs(session.closingBalanceCompany || 0);
                      updateBal('closingBalanceCompany', e.target.value === 'cr' ? -cur : cur);
                    }} style={{ width: 70 }}>
                    <option value="cr">CR</option><option value="dr">DR</option>
                  </select>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  Computed: {formatCurrency(Math.abs(compClosingComputed))} {compClosingComputed <= 0 ? 'CR' : 'DR'}
                  <span style={{ marginLeft: 8 }}>| Net Movement: {formatCurrency(compNetMovement)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recalculate + Expand/Collapse + Edit actions */}
      <div className="flex-between mb-2">
        <div className="flex gap-1">
          <button className="btn btn-teal" onClick={recalculate} style={{ fontWeight: 700, fontSize: 14 }}>
            🔄 Recalculate Statement
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => {
            if (expandedItems.size > 0 && expandedItems.size === items.length) {
              setExpandedItems(new Set());
            } else {
              setExpandedItems(new Set(items.map(i => i.id)));
            }
          }} style={{ fontSize: 12 }}>
            {expandedItems.size > 0 && expandedItems.size === items.length ? '⊟ Collapse All' : '⊞ Expand All'}
          </button>
        </div>
        <div className="flex gap-1">
          {editing ? (
            <button className="btn btn-success btn-sm" onClick={saveItems}>Save Changes</button>
          ) : (
            <button className="btn btn-outline btn-sm" onClick={() => setEditing(true)}>✏ Edit Items</button>
          )}
        </div>
      </div>

      {(() => {
        // Group items by source for clean presentation
        const addItems = items.filter(i => i.type === 'ADD');
        const lessItems = items.filter(i => i.type === 'LESS');
        const addSuppDR = addItems.filter(i => i.side === 'supplier');
        const addCompCR = addItems.filter(i => i.side === 'company');
        const addOther = addItems.filter(i => !i.side);
        const lessSuppCR = lessItems.filter(i => i.side === 'supplier');
        const lessCompDR = lessItems.filter(i => i.side === 'company');
        const lessOther = lessItems.filter(i => !i.side);

        const renderItem = (item) => (
          <div key={item.id}>
            <div className="reco-line" onClick={() => { const s = new Set(expandedItems); if (s.has(item.id)) s.delete(item.id); else s.add(item.id); setExpandedItems(s); }} style={{ cursor: 'pointer', background: expandedItems.has(item.id) ? 'var(--bg)' : 'transparent', borderBottom: expandedItems.has(item.id) ? 'none' : '', paddingLeft: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 10, color: 'var(--teal)', width: 12, textAlign: 'center' }}>{expandedItems.has(item.id) ? '▼' : '▶'}</span>
                {item.label} {item.count ? <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>({item.count} items)</span> : ''}
              </div>
              <div className="reco-amount" style={{ color: item.type === 'ADD' ? 'var(--green)' : 'var(--red)' }}>
                {item.type === 'ADD' ? '+' : '-'} {formatCurrency(item.amount)}
              </div>
            </div>
            {expandedItems.has(item.id) && <StatementDrilldown session={session} item={item} allCategories={allCategories} catMap={catMap} />}
          </div>
        );

        return (
          <div className="reco-statement">
            <div style={{ textAlign: 'center', fontSize: 18, fontWeight: 700, marginBottom: 20 }}>RECONCILIATION STATEMENT</div>
            <div style={{ fontSize: 13, marginBottom: 16, color: 'var(--text-secondary)' }}>
              {session.company} vs {session.supplier} • {session.period} • As on {formatDate(session.recoDate)}
            </div>

            {/* ── Supplier Closing Balance ── */}
            <div className="reco-line" style={{ fontWeight: 700, background: 'rgba(13,148,136,0.06)' }}>
              <div>Balance per Supplier Ledger (Closing)</div>
              <div className="reco-amount">{formatCurrency(suppPayable)} <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{suppClosingBal <= 0 ? 'CR' : 'DR'}</span></div>
            </div>
            {suppOpenBal !== 0 && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: '2px 16px 2px 28px' }}>
                (Opening: {formatCurrency(Math.abs(suppOpenBal))} {suppOpenBal < 0 ? 'CR' : 'DR'} + Net Movement: {formatCurrency(Math.abs(suppNetMovement))} {suppNetMovement >= 0 ? 'DR' : 'CR'})
              </div>
            )}

            {/* ── ADD Section ── */}
            {addItems.length > 0 && (
              <div style={{ borderLeft: '3px solid var(--green)', marginLeft: 8, marginTop: 8 }}>
                {addSuppDR.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', padding: '6px 16px 2px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Items in Supplier Only (DR)</div>
                    {addSuppDR.map(renderItem)}
                  </>
                )}
                {addCompCR.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', padding: '6px 16px 2px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Items in Company Only (CR)</div>
                    {addCompCR.map(renderItem)}
                  </>
                )}
                {addOther.length > 0 && addOther.map(renderItem)}
                <div className="reco-line" style={{ fontWeight: 600, background: 'rgba(34,197,94,0.06)', paddingLeft: 28 }}>
                  <div>Total ADD</div>
                  <div className="reco-amount" style={{ color: 'var(--green)' }}>+ {formatCurrency(addTotal)}</div>
                </div>
              </div>
            )}

            {/* ── LESS Section ── */}
            {lessItems.length > 0 && (
              <div style={{ borderLeft: '3px solid var(--red)', marginLeft: 8, marginTop: 8 }}>
                {lessSuppCR.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', padding: '6px 16px 2px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Items in Supplier Only (CR)</div>
                    {lessSuppCR.map(renderItem)}
                  </>
                )}
                {lessCompDR.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', padding: '6px 16px 2px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Items in Company Only (DR)</div>
                    {lessCompDR.map(renderItem)}
                  </>
                )}
                {lessOther.length > 0 && lessOther.map(renderItem)}
                <div className="reco-line" style={{ fontWeight: 600, background: 'rgba(239,68,68,0.06)', paddingLeft: 28 }}>
                  <div>Total LESS</div>
                  <div className="reco-amount" style={{ color: 'var(--red)' }}>- {formatCurrency(lessTotal)}</div>
                </div>
              </div>
            )}

            {/* ── Calculated Company Balance ── */}
            <div className="reco-line total" style={{ marginTop: 12 }}>
              <div>Balance per Company Ledger (Calculated)</div>
              <div className="reco-amount">{formatCurrency(calculatedAbs)} <span style={{ fontSize: 11 }}>{calculatedSide}</span></div>
            </div>

            {/* ── Actual Company Balance ── */}
            <div className="reco-line" style={{ fontWeight: 700, background: 'rgba(59,130,246,0.06)' }}>
              <div>Actual Company Ledger Balance (Closing)</div>
              <div className="reco-amount">{formatCurrency(compPayable)} <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{compClosingBal <= 0 ? 'CR' : 'DR'}</span></div>
            </div>
            {compOpenBal !== 0 && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: '2px 16px 2px 28px' }}>
                (Opening: {formatCurrency(Math.abs(compOpenBal))} {compOpenBal < 0 ? 'CR' : 'DR'} + Net Movement: {formatCurrency(Math.abs(compNetMovement))} {compNetMovement >= 0 ? 'DR' : 'CR'})
              </div>
            )}

            {/* ── Variance ── */}
            {variance > 0.01 ? (
              <div style={{ marginTop: 12, padding: 14, background: 'var(--red-bg)', borderRadius: 6, fontSize: 13 }}>
                <div className="flex-between">
                  <strong>Variance: {formatCurrency(variance)}</strong>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    Calculated {formatCurrency(calculatedAbs)} {calculatedSide} vs Actual {formatCurrency(compPayable)} {compClosingBal <= 0 ? 'CR' : 'DR'}
                  </span>
                </div>
                {calculatedSide !== (compClosingBal <= 0 ? 'CR' : 'DR') && (
                  <div style={{ fontSize: 12, color: 'var(--red)', fontWeight: 600, marginTop: 4 }}>⚠ DR/CR side mismatch — reconciliation items don't fully bridge the gap</div>
                )}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  This variance represents unreconciled differences. To reduce it:
                  match more items in the Manual tab, verify category tagging, or check closing balances.
                </div>
              </div>
            ) : (
              <div style={{ marginTop: 12, padding: 12, background: 'var(--green-bg)', borderRadius: 6, fontSize: 13 }}>
                <strong>✅ Reconciled — No Variance</strong>
              </div>
            )}
          </div>
        );
      })()}

      <div className="mt-2 text-right">
        {!editing ? (
          <div className="flex gap-1" style={{ justifyContent: 'flex-end' }}>
            <button className="btn btn-outline" onClick={() => setEditing(true)}>✏ Edit Statement</button>
            <button className="btn btn-teal" onClick={() => exportReconciliationExcel(session, allCategories, catMap)}>⬇ Download Excel Summary</button>
          </div>
        ) : (
          <div>
            <div className="flex-between mb-1"><h4>Reconciliation Items</h4><button className="btn btn-outline btn-sm" onClick={() => setItems([...items, { id: generateId(), label: '', amount: 0, type: 'ADD' }])}>+ Add Item</button></div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Label</th><th>Type</th><th>Amount</th><th></th></tr></thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={item.id}>
                      <td><input type="text" value={item.label} onChange={e => { const n = [...items]; n[idx] = { ...n[idx], label: e.target.value }; setItems(n); }} /></td>
                      <td><select value={item.type} onChange={e => { const n = [...items]; n[idx] = { ...n[idx], type: e.target.value }; setItems(n); }}><option value="ADD">ADD</option><option value="LESS">LESS</option></select></td>
                      <td><input type="number" value={item.amount} onChange={e => { const n = [...items]; n[idx] = { ...n[idx], amount: parseFloat(e.target.value) || 0 }; setItems(n); }} /></td>
                      <td><button className="btn btn-danger btn-xs" onClick={() => setItems(items.filter((_, i) => i !== idx))}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-1 mt-1"><button className="btn btn-success btn-sm" onClick={saveItems}>Save</button><button className="btn btn-outline btn-sm" onClick={() => setEditing(false)}>Cancel</button></div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Actions View ────────────────────────
function ActionsView({ session, onUpdate }) {
  const [actions, setActions] = useState(session.actionItems || []);
  const [title, setTitle] = useState('');
  const add = () => { if (!title.trim()) return; const updated = [...actions, { id: generateId(), title, priority: 'medium', status: 'pending', dueDate: '', createdAt: new Date().toISOString() }]; setActions(updated); onUpdate({ ...session, actionItems: updated }); setTitle(''); };
  const updateStatus = (id, status) => { const updated = actions.map(a => a.id === id ? { ...a, status } : a); setActions(updated); onUpdate({ ...session, actionItems: updated }); };
  const remove = (id) => { const updated = actions.filter(a => a.id !== id); setActions(updated); onUpdate({ ...session, actionItems: updated }); };
  const priorityColors = { high: 'var(--red)', medium: 'var(--amber)', low: 'var(--green)' };

  const sessionStatuses = ['draft', 'in-progress', 'completed', 'approved'];
  const statusColors = { draft: '#6b7280', 'in-progress': '#f59e0b', completed: '#22c55e', approved: '#0d9488' };
  const statusLabels = { draft: 'Draft', 'in-progress': 'In Progress', completed: 'Completed', approved: 'Approved' };
  const statusIcons = { draft: '📋', 'in-progress': '🔄', completed: '✅', approved: '🔒' };

  const changeSessionStatus = (newStatus) => {
    onUpdate({ ...session, status: newStatus, statusChangedAt: new Date().toISOString() });
  };

  return (
    <div>
      {/* ── Session Status Management ── */}
      <div className="card mb-2" style={{ padding: 20, border: `2px solid ${statusColors[session.status] || '#ccc'}`, background: `${statusColors[session.status]}10` }}>
        <div className="flex-between" style={{ marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Session Status</h3>
          <span style={{ fontSize: 24 }}>{statusIcons[session.status] || ''} <strong style={{ color: statusColors[session.status], fontSize: 16 }}>{statusLabels[session.status] || session.status}</strong></span>
        </div>

        {/* Status progression buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 16 }}>
          {sessionStatuses.map((st, i) => {
            const isCurrent = session.status === st;
            const isPast = sessionStatuses.indexOf(session.status) > i;
            return (
              <React.Fragment key={st}>
                <button
                  onClick={() => changeSessionStatus(st)}
                  style={{
                    padding: '8px 18px', fontSize: 13, fontWeight: isCurrent ? 700 : 500,
                    background: isCurrent ? statusColors[st] : isPast ? statusColors[st] + '30' : '#f3f4f6',
                    color: isCurrent ? '#fff' : isPast ? statusColors[st] : '#9ca3af',
                    border: `2px solid ${isCurrent ? statusColors[st] : isPast ? statusColors[st] + '60' : '#e5e7eb'}`,
                    borderRadius: i === 0 ? '6px 0 0 6px' : i === sessionStatuses.length - 1 ? '0 6px 6px 0' : 0,
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >
                  {statusIcons[st]} {statusLabels[st]}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          <strong>Workflow:</strong> Draft → In Progress (upload & match) → Completed (reconciliation done) → Approved (signed off)
          {session.statusChangedAt && <span style={{ marginLeft: 12 }}>Last changed: {new Date(session.statusChangedAt).toLocaleDateString()}</span>}
        </div>
      </div>

      <h3 style={{ marginBottom: 16 }}>Action Tracker</h3>
      <div className="flex gap-1 mb-2">
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Add action item..." style={{ flex: 1 }} onKeyDown={e => e.key === 'Enter' && add()} />
        <button className="btn btn-teal" onClick={add}>+ Add</button>
      </div>
      {actions.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Action Item</th><th>Priority</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {actions.map(a => (
                <tr key={a.id}>
                  <td style={{ textDecoration: a.status === 'completed' ? 'line-through' : 'none' }}>{a.title}</td>
                  <td><span style={{ color: priorityColors[a.priority], fontWeight: 600, textTransform: 'capitalize' }}>{a.priority}</span></td>
                  <td><select value={a.status} onChange={e => updateStatus(a.id, e.target.value)} style={{ width: 120, padding: '4px 8px' }}>
                    <option value="pending">Pending</option><option value="in-progress">In Progress</option><option value="completed">Completed</option>
                  </select></td>
                  <td><button className="btn btn-danger btn-xs" onClick={() => remove(a.id)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <div className="empty-state"><div className="empty-icon">📝</div><div className="empty-title">No action items</div></div>}
    </div>
  );
}

// ══════════════════════════════════════════════════
//  CATEGORY MAPPING VIEW
//  - Auto-detect categories from transaction type
//  - Manual override: single or bulk tagging
//  - Opposite-party mapping (editable)
//  - Side-by-side totals with drill-down
//  - Custom category support
// ══════════════════════════════════════════════════
function CategoryMappingView({ session, onUpdate, toast }) {
  const allCategories = [...DEFAULT_CATEGORIES, ...(session.customCategories || []).map(c => ({ id: c.id, label: c.label, color: c.color || '#6b7280' }))];
  const catMap = session.categoryMapping || { ...DEFAULT_CATEGORY_MAPPING };
  const [subTab, setSubTab] = useState('overview'); // overview | company | supplier | mapping | custom
  const [drillCat, setDrillCat] = useState(null);
  const [drillSide, setDrillSide] = useState(null);
  const [selectedComp, setSelectedComp] = useState(new Set());
  const [selectedSupp, setSelectedSupp] = useState(new Set());
  const [bulkCat, setBulkCat] = useState('');
  const [filterCatComp, setFilterCatComp] = useState('all');
  const [filterCatSupp, setFilterCatSupp] = useState('all');
  const [searchComp, setSearchComp] = useState('');
  const [searchSupp, setSearchSupp] = useState('');
  const [groupComp, setGroupComp] = useState(false);
  const [groupSupp, setGroupSupp] = useState(false);

  // Auto-detect: assign categoryId to each transaction if not already set
  const ensureCategories = (ledger) => {
    return ledger.map(tx => ({
      ...tx,
      categoryId: tx.categoryId || typeToCategoryId(tx.type),
    }));
  };

  // Initialize categories on mount
  useEffect(() => {
    const cL = ensureCategories(session.companyLedger);
    const sL = ensureCategories(session.supplierLedger);
    const needsUpdate = cL.some((t, i) => t.categoryId !== session.companyLedger[i]?.categoryId) ||
      sL.some((t, i) => t.categoryId !== session.supplierLedger[i]?.categoryId);
    if (needsUpdate) {
      onUpdate({ ...session, companyLedger: cL, supplierLedger: sL });
    }
  }, []); // eslint-disable-line

  const compLedger = ensureCategories(session.companyLedger);
  const suppLedger = ensureCategories(session.supplierLedger);

  // Category-wise aggregation
  const aggregate = (ledger) => {
    const agg = {};
    allCategories.forEach(c => { agg[c.id] = { count: 0, debit: 0, credit: 0, net: 0 }; });
    ledger.forEach(tx => {
      const cid = tx.categoryId || 'other';
      if (!agg[cid]) agg[cid] = { count: 0, debit: 0, credit: 0, net: 0 };
      agg[cid].count++;
      agg[cid].debit += (tx.debit || 0);
      agg[cid].credit += (tx.credit || 0);
      agg[cid].net += (tx.debit || 0) - (tx.credit || 0);
    });
    return agg;
  };

  const compAgg = aggregate(compLedger);
  const suppAgg = aggregate(suppLedger);

  // Update a transaction's category (or array of transactions)
  const setTxCategory = (side, idOrIds, catId) => {
    const key = side === 'company' ? 'companyLedger' : 'supplierLedger';
    const ids = Array.isArray(idOrIds) ? new Set(idOrIds) : new Set([idOrIds]);
    const updated = session[key].map(tx => ids.has(tx.id) ? { ...tx, categoryId: catId } : tx);
    onUpdate({ ...session, [key]: updated });
  };

  // Bulk tag selected transactions
  const bulkTag = (side) => {
    if (!bulkCat) { toast('Select a category first', 'error'); return; }
    const key = side === 'company' ? 'companyLedger' : 'supplierLedger';
    const selected = side === 'company' ? selectedComp : selectedSupp;
    if (selected.size === 0) { toast('Select transactions first', 'error'); return; }
    const updated = session[key].map(tx => selected.has(tx.id) ? { ...tx, categoryId: bulkCat } : tx);
    onUpdate({ ...session, [key]: updated });
    if (side === 'company') setSelectedComp(new Set()); else setSelectedSupp(new Set());
    toast(`Tagged ${selected.size} transactions as ${allCategories.find(c => c.id === bulkCat)?.label || bulkCat}`);
  };

  // Toggle select all for a side
  const toggleSelectAll = (side, ids) => {
    const sel = side === 'company' ? selectedComp : selectedSupp;
    const setter = side === 'company' ? setSelectedComp : setSelectedSupp;
    if (ids.every(id => sel.has(id))) setter(new Set());
    else setter(new Set(ids));
  };

  // Update opposite-party mapping
  const updateMapping = (compCat, suppCat) => {
    const newMap = { ...catMap, [compCat]: suppCat };
    onUpdate({ ...session, categoryMapping: newMap });
    toast(`Mapped Company "${compCat}" → Supplier "${suppCat}"`);
  };

  // Add custom category
  const [newCatLabel, setNewCatLabel] = useState('');
  const addCustomCategory = () => {
    if (!newCatLabel.trim()) return;
    const id = newCatLabel.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (allCategories.find(c => c.id === id)) { toast('Category already exists', 'error'); return; }
    const custom = [...(session.customCategories || []), { id, label: newCatLabel.trim(), color: '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0') }];
    const newMap = { ...catMap, [id]: 'other' };
    onUpdate({ ...session, customCategories: custom, categoryMapping: newMap });
    setNewCatLabel('');
    toast(`Added category "${newCatLabel.trim()}"`);
  };

  const removeCustomCategory = (id) => {
    const custom = (session.customCategories || []).filter(c => c.id !== id);
    // Reset any transactions tagged with this category
    const cL = session.companyLedger.map(tx => tx.categoryId === id ? { ...tx, categoryId: 'other' } : tx);
    const sL = session.supplierLedger.map(tx => tx.categoryId === id ? { ...tx, categoryId: 'other' } : tx);
    const newMap = { ...catMap }; delete newMap[id];
    onUpdate({ ...session, customCategories: custom, categoryMapping: newMap, companyLedger: cL, supplierLedger: sL });
    toast('Category removed');
  };

  // ── Sub-tab: Overview (side-by-side totals) ──
  const renderOverview = () => {
    const activeCats = allCategories.filter(c => (compAgg[c.id]?.count || 0) + (suppAgg[c.id]?.count || 0) > 0);
    return (
      <div>
        <h3 style={{ marginBottom: 16 }}>Category-wise Comparison</h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th className="text-right">Company Count</th>
                <th className="text-right">Company DR</th>
                <th className="text-right">Company CR</th>
                <th className="text-right">Company Net</th>
                <th style={{ borderLeft: '3px solid var(--teal)', paddingLeft: 16 }}>Supplier Opposite</th>
                <th className="text-right">Supp Count</th>
                <th className="text-right">Supp DR</th>
                <th className="text-right">Supp CR</th>
                <th className="text-right">Supp Net</th>
                <th className="text-right" style={{ borderLeft: '3px solid var(--red)', paddingLeft: 16 }}>Variance</th>
              </tr>
            </thead>
            <tbody>
              {activeCats.map(cat => {
                const ca = compAgg[cat.id] || { count: 0, debit: 0, credit: 0, net: 0 };
                const oppCatId = catMap[cat.id] || cat.id;
                const sa = suppAgg[oppCatId] || { count: 0, debit: 0, credit: 0, net: 0 };
                const oppCat = allCategories.find(c => c.id === oppCatId);
                // Variance = difference between absolute NET amounts
                // Company Payment (DR=positive net) maps to Supplier Receipt (CR=negative net)
                // Company Purchase (CR=negative net) maps to Supplier Sales (DR=positive net)
                // They represent the SAME economic amount but opposite signs, so compare absolutes
                const variance = Math.abs(ca.net) - Math.abs(sa.net);
                return (
                  <tr key={cat.id} style={{ cursor: 'pointer' }} onClick={() => { setDrillCat(cat.id); setDrillSide('both'); setSubTab('overview'); }}>
                    <td>
                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: cat.color, marginRight: 8 }} />
                      <strong>{cat.label}</strong>
                    </td>
                    <td className="text-right">{ca.count}</td>
                    <td className="currency">{ca.debit ? formatCurrency(ca.debit) : '—'}</td>
                    <td className="currency">{ca.credit ? formatCurrency(ca.credit) : '—'}</td>
                    <td className="currency" style={{ fontWeight: 600 }}>{formatCurrency(ca.net)}</td>
                    <td style={{ borderLeft: '3px solid var(--teal)', paddingLeft: 16 }}>
                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: oppCat?.color || '#999', marginRight: 8 }} />
                      {oppCat?.label || oppCatId}
                    </td>
                    <td className="text-right">{sa.count}</td>
                    <td className="currency">{sa.debit ? formatCurrency(sa.debit) : '—'}</td>
                    <td className="currency">{sa.credit ? formatCurrency(sa.credit) : '—'}</td>
                    <td className="currency" style={{ fontWeight: 600 }}>{formatCurrency(sa.net)}</td>
                    <td className="currency" style={{ fontWeight: 700, borderLeft: '3px solid var(--red)', paddingLeft: 16, color: Math.abs(variance) > 1 ? 'var(--red)' : 'var(--green)' }}>
                      {formatCurrency(variance)}
                    </td>
                  </tr>
                );
              })}
              {/* Totals row */}
              <tr style={{ fontWeight: 700, background: 'var(--bg)' }}>
                <td>TOTAL</td>
                <td className="text-right">{compLedger.length}</td>
                <td className="currency">{formatCurrency(compLedger.reduce((s, t) => s + (t.debit || 0), 0))}</td>
                <td className="currency">{formatCurrency(compLedger.reduce((s, t) => s + (t.credit || 0), 0))}</td>
                <td className="currency">{formatCurrency(compLedger.reduce((s, t) => s + (t.debit || 0) - (t.credit || 0), 0))}</td>
                <td style={{ borderLeft: '3px solid var(--teal)', paddingLeft: 16 }}>TOTAL</td>
                <td className="text-right">{suppLedger.length}</td>
                <td className="currency">{formatCurrency(suppLedger.reduce((s, t) => s + (t.debit || 0), 0))}</td>
                <td className="currency">{formatCurrency(suppLedger.reduce((s, t) => s + (t.credit || 0), 0))}</td>
                <td className="currency">{formatCurrency(suppLedger.reduce((s, t) => s + (t.debit || 0) - (t.credit || 0), 0))}</td>
                {(() => {
                  const totalVar = activeCats.reduce((sum, cat) => {
                    const ca2 = compAgg[cat.id] || { net: 0 };
                    const oppId2 = catMap[cat.id] || cat.id;
                    const sa2 = suppAgg[oppId2] || { net: 0 };
                    return sum + (Math.abs(ca2.net) - Math.abs(sa2.net));
                  }, 0);
                  return (
                    <td className="currency" style={{ borderLeft: '3px solid var(--red)', paddingLeft: 16, fontWeight: 700, color: Math.abs(totalVar) > 1 ? 'var(--red)' : 'var(--green)' }}>
                      {formatCurrency(totalVar)}
                    </td>
                  );
                })()}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Drill-down */}
        {drillCat && (() => {
          const cat = allCategories.find(c => c.id === drillCat);
          const oppCatId = catMap[drillCat] || drillCat;
          const compItems = compLedger.filter(t => t.categoryId === drillCat);
          const suppItems = suppLedger.filter(t => t.categoryId === oppCatId);
          return (
            <div className="mt-2 card" style={{ background: 'var(--bg)' }}>
              <div className="flex-between mb-1">
                <h4>Drill-down: {cat?.label} (Company) ↔ {allCategories.find(c => c.id === oppCatId)?.label} (Supplier)</h4>
                <button className="btn btn-outline btn-xs" onClick={() => setDrillCat(null)}>✕ Close</button>
              </div>
              <div className="side-by-side">
                <div>
                  <h5 style={{ marginBottom: 8 }}>Company ({compItems.length} items, {formatCurrency(compItems.reduce((s, t) => s + (t.debit || 0) + (t.credit || 0), 0))})</h5>
                  <div className="table-wrap" style={{ maxHeight: 300, overflowY: 'auto' }}>
                    <table><thead><tr><th>Date</th><th>Doc</th><th>DR</th><th>CR</th></tr></thead>
                      <tbody>{compItems.map(t => (
                        <tr key={t.id}><td>{formatDate(t.date)}</td><td>{t.docNo}</td>
                          <td className="currency">{t.debit ? formatCurrency(t.debit) : '—'}</td>
                          <td className="currency">{t.credit ? formatCurrency(t.credit) : '—'}</td></tr>
                      ))}</tbody></table>
                  </div>
                </div>
                <div>
                  <h5 style={{ marginBottom: 8 }}>Supplier ({suppItems.length} items, {formatCurrency(suppItems.reduce((s, t) => s + (t.debit || 0) + (t.credit || 0), 0))})</h5>
                  <div className="table-wrap" style={{ maxHeight: 300, overflowY: 'auto' }}>
                    <table><thead><tr><th>Date</th><th>Doc</th><th>DR</th><th>CR</th></tr></thead>
                      <tbody>{suppItems.map(t => (
                        <tr key={t.id}><td>{formatDate(t.date)}</td><td>{t.docNo}</td>
                          <td className="currency">{t.debit ? formatCurrency(t.debit) : '—'}</td>
                          <td className="currency">{t.credit ? formatCurrency(t.credit) : '—'}</td></tr>
                      ))}</tbody></table>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  // ── Sub-tab: Ledger tagging (company or supplier) ──
  const renderLedgerTagging = (side) => {
    const ledger = side === 'company' ? compLedger : suppLedger;
    const selected = side === 'company' ? selectedComp : selectedSupp;
    const setSelected = side === 'company' ? setSelectedComp : setSelectedSupp;
    const isGrouped = side === 'company' ? groupComp : groupSupp;
    const setIsGrouped = side === 'company' ? setGroupComp : setGroupSupp;
    const filterCat = side === 'company' ? filterCatComp : filterCatSupp;
    const setFilterCat = side === 'company' ? setFilterCatComp : setFilterCatSupp;
    const search = side === 'company' ? searchComp : searchSupp;
    const setSearch = side === 'company' ? setSearchComp : setSearchSupp;

    const filtered = ledger.filter(t => {
      const matchCat = filterCat === 'all' || t.categoryId === filterCat;
      const matchSearch = !search || t.docNo?.toLowerCase().includes(search.toLowerCase()) || t.narration?.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });

    let displayList = [];
    if (isGrouped) {
      const groups = {};
      filtered.forEach(t => {
        const key = t.docNo || t.id;
        if (!groups[key]) groups[key] = { id: key, isGroup: true, txns: [], date: t.date, docNo: t.docNo, narration: t.narration, debit: 0, credit: 0, type: t.type, categoryId: t.categoryId };
        groups[key].txns.push(t.id);
        groups[key].debit += (t.debit || 0);
        groups[key].credit += (t.credit || 0);
      });
      // Convert to NET amounts — show only debit OR credit, not both
      Object.values(groups).forEach(g => {
        if (g.txns.length > 1) {
          const net = g.debit - g.credit;
          if (net >= 0) { g.debit = Math.round(net * 100) / 100; g.credit = 0; }
          else { g.debit = 0; g.credit = Math.round(Math.abs(net) * 100) / 100; }
        }
      });
      displayList = Object.values(groups);
    } else {
      displayList = filtered.map(t => ({ ...t, txns: [t.id] }));
    }

    const allTxnIds = displayList.flatMap(d => d.txns);
    const allSelected = allTxnIds.length > 0 && allTxnIds.every(id => selected.has(id));

    const toggleDisplayItem = (item) => {
      const next = new Set(selected);
      const allInItem = item.txns.every(id => selected.has(id));
      item.txns.forEach(id => { if (allInItem) next.delete(id); else next.add(id); });
      setSelected(next);
    };

    return (
      <div>
        <div className="flex-between mb-2">
          <h3>{side === 'company' ? 'Company' : 'Supplier'} Ledger – Tag Categories</h3>
          <div className="flex gap-1 flex-wrap" style={{ alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--teal)' }}>
              <input type="checkbox" checked={isGrouped} onChange={e => setIsGrouped(e.target.checked)} style={{ transform: 'scale(1.1)' }} /> Group by Doc No
            </label>
            <input type="text" placeholder="Search Doc/Narration..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 180 }} />
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ width: 160 }}>
              <option value="all">All Categories</option>
              {allCategories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>|</span>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{selected.size} selected</span>
            <select value={bulkCat} onChange={e => setBulkCat(e.target.value)} style={{ width: 160 }}>
              <option value="">Bulk tag as...</option>
              {allCategories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <button className="btn btn-teal btn-sm" onClick={() => bulkTag(side)} disabled={!bulkCat || selected.size === 0}>
              Tag {selected.size} items
            </button>
          </div>
        </div>
        <div className="table-wrap" style={{ maxHeight: 500, overflowY: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 30 }}><input type="checkbox" checked={allSelected} onChange={() => toggleSelectAll(side, allTxnIds)} /></th>
                <th>Date</th><th>Doc No</th><th>Narration</th><th>Debit</th><th>Credit</th><th>Auto Type</th><th>Category</th>
              </tr>
            </thead>
            <tbody>
              {displayList.map(item => {
                const cat = allCategories.find(c => c.id === item.categoryId);
                const isSelected = item.txns.every(id => selected.has(id));
                const isPartial = !isSelected && item.txns.some(id => selected.has(id));
                return (
                  <tr key={item.id} style={{ background: isSelected ? 'rgba(13,148,136,0.08)' : 'transparent' }}>
                    <td><input type="checkbox" checked={isSelected} ref={el => { if (el) el.indeterminate = isPartial; }} onChange={() => toggleDisplayItem(item)} /></td>
                    <td>{formatDate(item.date)}</td>
                    <td><strong>{item.docNo}</strong> {item.isGroup && item.txns.length > 1 && <span className="badge badge-warning" style={{ marginLeft: 4 }}>x{item.txns.length} ITEMS</span>}</td>
                    <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.narration}</td>
                    <td className="currency">{item.debit ? formatCurrency(item.debit) : '—'}</td>
                    <td className="currency">{item.credit ? formatCurrency(item.credit) : '—'}</td>
                    <td><span className="badge badge-neutral">{item.type}</span></td>
                    <td>
                      <select
                        value={item.categoryId || 'other'}
                        onChange={e => setTxCategory(side, item.txns, e.target.value)}
                        style={{ padding: '3px 6px', fontSize: 12, width: 130, borderColor: cat?.color || 'var(--border)', color: cat?.color || 'var(--text)' }}
                      >
                        {allCategories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Sub-tab: Opposite-Party Mapping ──
  const renderMapping = () => (
    <div>
      <h3 style={{ marginBottom: 16 }}>Opposite-Party Mapping</h3>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
        Define how company categories correspond to supplier categories. This drives category-wise totals comparison and helps the matching engine.
      </p>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Company Category</th><th style={{ textAlign: 'center' }}>→</th><th>Supplier Category (Opposite)</th></tr></thead>
          <tbody>
            {allCategories.map(cat => (
              <tr key={cat.id}>
                <td>
                  <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: cat.color, marginRight: 8 }} />
                  <strong>{cat.label}</strong>
                </td>
                <td style={{ textAlign: 'center', fontSize: 18 }}>↔</td>
                <td>
                  <select value={catMap[cat.id] || 'other'} onChange={e => updateMapping(cat.id, e.target.value)} style={{ width: 200 }}>
                    {allCategories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ── Sub-tab: Custom Categories ──
  const renderCustom = () => (
    <div>
      <h3 style={{ marginBottom: 16 }}>Custom Categories</h3>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
        Add categories specific to this supplier (e.g., "Consumable Charges", "GST Non-filing", "Brand Registration").
      </p>
      <div className="flex gap-1 mb-2">
        <input type="text" value={newCatLabel} onChange={e => setNewCatLabel(e.target.value)} placeholder="New category name..." style={{ width: 250 }} onKeyDown={e => e.key === 'Enter' && addCustomCategory()} />
        <button className="btn btn-teal" onClick={addCustomCategory}>+ Add Category</button>
      </div>
      {(session.customCategories || []).length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Category</th><th>ID</th><th>Company Txns</th><th>Supplier Txns</th><th>Mapped To</th><th></th></tr></thead>
            <tbody>
              {(session.customCategories || []).map(c => (
                <tr key={c.id}>
                  <td><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: c.color, marginRight: 8 }} /><strong>{c.label}</strong></td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{c.id}</td>
                  <td className="text-right">{compAgg[c.id]?.count || 0}</td>
                  <td className="text-right">{suppAgg[c.id]?.count || 0}</td>
                  <td>{allCategories.find(x => x.id === (catMap[c.id] || 'other'))?.label || 'Other'}</td>
                  <td><button className="btn btn-danger btn-xs" onClick={() => removeCustomCategory(c.id)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>No custom categories yet. Default categories cover most cases.</div>
      )}

      <div className="mt-3">
        <h4 style={{ marginBottom: 8 }}>All Available Categories ({allCategories.length})</h4>
        <div className="flex gap-1 flex-wrap">
          {allCategories.map(c => (
            <span key={c.id} className="badge" style={{ background: c.color + '20', color: c.color, border: `1px solid ${c.color}40`, padding: '5px 12px', fontSize: 12 }}>
              {c.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex gap-1 mb-2 flex-wrap">
        {[
          { id: 'overview', label: '📊 Category Totals' },
          { id: 'company', label: '🏢 Tag Company' },
          { id: 'supplier', label: '🏭 Tag Supplier' },
          { id: 'mapping', label: '↔ Opposite Mapping' },
          { id: 'custom', label: '+ Custom Categories' },
        ].map(st => (
          <button key={st.id} className={`btn btn-sm ${subTab === st.id ? 'btn-teal' : 'btn-outline'}`} onClick={() => setSubTab(st.id)}>
            {st.label}
          </button>
        ))}
      </div>

      {subTab === 'overview' && renderOverview()}
      {subTab === 'company' && renderLedgerTagging('company')}
      {subTab === 'supplier' && renderLedgerTagging('supplier')}
      {subTab === 'mapping' && renderMapping()}
      {subTab === 'custom' && renderCustom()}
    </div>
  );
}

// ── Notes View ──────────────────────────
function NotesView({ session, onUpdate, onSave }) {
  const [notes, setNotes] = useState(session.notes || '');
  return (
    <div>
      <h3 style={{ marginBottom: 16 }}>Notes & History</h3>
      <div className="form-group"><textarea value={notes} onChange={e => { setNotes(e.target.value); onUpdate({ ...session, notes: e.target.value }); }} placeholder="Add notes about this reconciliation..." rows={8} /></div>
      <button className="btn btn-teal" onClick={onSave}>Save Notes</button>
    </div>
  );
}

export default App;
